import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  formatCurrency,
  formatDate,
  formatTime,
  calculateDurationHours,
  parseDateOnly,
  isPastBookingDate,
  getYmdInTimezone,
  formatLocalCalendarYmd
} from '../utils/dateUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { FigmaTrashIcon, VenueDocumentIcon, FigmaManageDrawerNote2Icon, FigmaManageDrawerShoppingBagIcon, FigmaManageDrawerDocumentTextIcon, FigmaManageDrawerRepeatIcon } from '../../icons';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import discountCodeApiService from '../../../services/discountCodeApi';
import messageThreadsApiService from '../../../services/messageThreadsApi';
import { buildBookingThreadTopic, getBackendUserId } from '../../../utils/messageThreadDisplay';
import '../styles/ManageBookings.css';
import mobileBackIcon from '../../icons/figma/manage-booking/arrow-down.svg';
import mobileCloseIcon from '../../icons/figma/manage-booking/close.svg';
import { resolveBookingPaymentContext, paymentEmailFeedbackMessage, shouldSendPaymentEmailAfterInvoiceUpload, isApprovedBookingStatus, buildApprovalEmailParams, getPaymentReminderSentCount, getFeedbackRequestSentCount, needsVenuePricingConfirmation, bookingNeedsOrganizerPayment, shouldStartInPaymentEditMode, FREE_BOOKING_MODE, resolveFreeBookingMode, resolveChargeableVenueSubtotal, formatFreeBookingModeLabel, buildFreeBookingModeRequirements, normalizeFreeBookingMode } from '../../../utils/bookingPayment';
import { computePaymentFeeBreakdown, isManualBookingSource, buildFeeBreakdownSnapshot, extractFeeBreakdownSnapshot, formatFeeBreakdownLines } from '../../../utils/bookingPaymentFees';
import { parseBookingRequirements, getBookingPackageLines, getBookingRecurringInfo } from '../utils/bookingAddOns';
import { isPerPersonBookingEnabled, resolveBookingTypeFromRecord } from '../../../utils/bookingPricingMetadata';
import {
  resolveBookingAmounts,
  resolveBookingAmountPaid,
  resolveBookingPaymentDisplayFields,
  resolveBookingPayableBreakdown
} from '../../../utils/bookingAmounts';
import {
  normalizeBookingModeApi,
  resolveEditedBookingBaseAmount,
  sumDraftPackageLines
} from '../../../utils/bookingEditPricing';
import { mergeBookingLocalUpdate, pickDirtyBookingUpdatePayload } from '../../../utils/bookingDirtyUpdate';
import { bookingHeadcount, bookingWindowsOverlap, maxHeadcountAfterOthers } from '../../../utils/bookingHeadcount';
import {
  buildAdminBookingViewPath,
  buildAdminOrganizerProfilePath,
  isAdminUser
} from '../../../utils/adminUser';
import {
  bookingHasListedSpaceRate,
  buildPricingMetadata,
  computeBaseFromPricingMetadata,
  formatPricingBreakdownLabel,
  readPricingMetadata,
  resolveConfirmedPerPersonRate,
  resolveConfirmedSpaceHourlyRate
} from '../../../utils/bookingPricingMetadata';
import { getAddonUnitLabel } from '../../../utils/addonPricing';
import {
  draftPricingFromLastPricedSnapshot,
  lastPricedSnapshotFromDraftPricing,
  lastPricedSnapshotHasCharge,
  parseLastPricedSnapshot,
  resolveInitialPricingRef,
  resolveLastPricedSnapshotForFreeSave
} from '../../../utils/bookingLastPricedSnapshot';
import { resolveBookingOrganizerContact } from '../../../utils/bookingDisplay';
import {
  isInHouseCateringPreference,
  formatBookingDietaryRestrictions,
  bookingNeedsAv,
  formatBookingAv
} from '../../../utils/bookingExportDetails';
import TimeRangePicker from '../../Common/TimeRangePicker';
import CustomDatePicker from '../../Common/CustomDatePicker';

const paymentLabels = {
  pending: 'Pending',
  completed: 'Paid',
  paid: 'Paid',
  refunded: 'Refunded',
  1: 'Paid',
  0: 'Pending'
};

const TAB_BOOKING = 'booking';
const TAB_ADDONS = 'addons';
const TAB_NOTES = 'notes';
const TAB_CHANGELOG = 'changelog';

const MOBILE_SECTION_TITLES = {
  requirements: 'Event Requirements',
  addons: 'Add-ons',
  notes: 'Notes',
  changelog: 'Change log',
  breakdown: 'Price breakdown',
  payment: 'Payment details',
  message: 'Booking',
  cancel: 'Cancel booking'
};

const PRICE_TAB_BREAKDOWN = 'breakdown';
const PRICE_TAB_PAYMENT = 'payment';

const PAYMENT_MODE_OPTIONS = [
  'Bank transfer',
  'Cash',
  'Card',
  'Stripe',
  'Cheque',
  'Other'
];

const PAYMENT_DRAFT_CHANGE_LABELS = new Set([
  'Amount paid',
  'Amount pending',
  'Payment method',
  'Transaction ID'
]);

const LEFT_PANEL_DRAFT_CHANGE_LABELS = new Set([
  'Event name',
  'Event date',
  'Start time',
  'End time',
  'Pricing mode',
  'Event activity',
  'Guests',
  'Catering',
  'Dietary requirements',
  'Audio/visual (A/V)',
  'Alcohol',
  'Add-ons'
]);

const RIGHT_PANEL_PRICE_CHANGE_LABELS = new Set([
  'Booking amount',
  'Discount',
  'Discount code',
  'Pricing waiver'
]);

const bookingStatusLabels = {
  pending: 'Pending',
  confirmed: 'Accepted',
  completed: 'Completed',
  on_hold: 'On hold',
  canceled_by_user: 'Cancelled',
  canceled_by_owner: 'Cancelled',
  cancelled: 'Cancelled'
};

const auditActionLabels = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  other: 'Changed'
};

const resolveAuditActionLabel = (entry) => {
  const method = String(entry?.http_method || '').toUpperCase();
  const path = String(entry?.path || '').split('?')[0];
  if (method === 'DELETE') return 'Deleted';
  if (method === 'POST' && /^\/bookings\/?$/.test(path)) return 'Created';
  if (method === 'PATCH' || method === 'PUT') return 'Updated';
  if (method === 'POST' && /^\/bookings\/[^/]+/.test(path)) return 'Updated';
  return auditActionLabels[entry?.action] || 'Changed';
};

const formatAuditEntryTitle = (entry) => {
  const actionLabel = resolveAuditActionLabel(entry);
  const body = entry?.request_body;
  const changedKeys =
    body && typeof body === 'object' && !Array.isArray(body)
      ? Object.keys(body).filter((key) => !key.startsWith('_') && key !== 'fee_breakdown')
      : [];
  if (entry?.entity_type === 'booking' && changedKeys.length) {
    return `${actionLabel} booking: ${changedKeys.join(', ')}`;
  }
  if (entry?.http_method && entry?.path) {
    return `${actionLabel} via ${entry.http_method} ${entry.path}`;
  }
  return `${actionLabel} record`;
};

const formatAuditEntryDetail = (entry) => {
  const actor = entry?.actor_email || 'Unknown user';
  const status = entry?.response_status != null ? ` · HTTP ${entry.response_status}` : '';
  return `by ${actor}${status}`;
};

const changelogTimestamp = (value) => {
  if (!value) return 0;
  let raw = value;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
      raw = `${trimmed.replace(' ', 'T')}Z`;
    }
  }
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
};

const formatChangelogDateTime = (value) => {
  const at = changelogTimestamp(value);
  if (!at) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(at));
};

const buildSortedChangelogEntries = (auditLogEntries, booking, bookingCreatedLabel) => {
  const items = [];
  const entries = Array.isArray(auditLogEntries) ? auditLogEntries : [];

  entries.forEach((entry, apiOrder) => {
    const detailParts = [formatAuditEntryDetail(entry)];
    if (entry.created_at) {
      detailParts.push(formatChangelogDateTime(entry.created_at));
    }
    items.push({
      key: entry.id || `${entry.path}-${entry.created_at}-${apiOrder}`,
      title: formatAuditEntryTitle(entry),
      detail: detailParts.filter(Boolean).join(' · '),
      feeLines: formatFeeBreakdownLines(extractFeeBreakdownSnapshot(entry.request_body)),
      at: changelogTimestamp(entry.created_at),
      apiOrder
    });
  });

  const hasInitialCreateAudit = entries.some((entry) => {
    const method = String(entry?.http_method || '').toUpperCase();
    const path = String(entry?.path || '').split('?')[0];
    return method === 'POST' && /^\/bookings\/?$/.test(path);
  });

  if (booking?.created_at && !hasInitialCreateAudit) {
    items.push({
      key: 'booking-created',
      title: 'Booking created',
      detail: bookingCreatedLabel,
      feeLines: formatFeeBreakdownLines(extractFeeBreakdownSnapshot(booking)),
      at: changelogTimestamp(booking.created_at),
      apiOrder: Number.MAX_SAFE_INTEGER
    });
  }

  return items.sort((a, b) => {
    const byTime = b.at - a.at;
    if (byTime !== 0) return byTime;
    return a.apiOrder - b.apiOrder;
  });
};

const CANCELLED_BOOKING_STATUSES = new Set([
  'canceled_by_user',
  'canceled_by_owner',
  'cancelled',
  'cancelled_by_user',
  'cancelled_by_owner'
]);

const DRAWER_TAB_ICONS = {
  requirements: FigmaManageDrawerNote2Icon,
  addons: FigmaManageDrawerShoppingBagIcon,
  notes: FigmaManageDrawerDocumentTextIcon,
  changelog: FigmaManageDrawerRepeatIcon
};

const DrawerTabIcon = ({ name }) => {
  const Icon = DRAWER_TAB_ICONS[name] || DRAWER_TAB_ICONS.changelog;
  return <Icon className="manage-drawer-pill-tab-icon" aria-hidden />;
};

const SidebarChevron = ({ open }) => (
  <svg
    className={`manage-drawer-sidebar-chevron${open ? ' manage-drawer-sidebar-chevron--open' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
  >
    <path d="M4 6l4 4 4-4" stroke="#585858" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PaymentEmptyIcon = () => (
  <svg className="manage-drawer-payment-empty-icon" viewBox="0 0 48 48" fill="none" aria-hidden>
    <rect x="6" y="12" width="36" height="24" rx="4" fill="currentColor" opacity="0.15" />
    <rect x="6" y="12" width="36" height="24" rx="4" stroke="currentColor" strokeWidth="2" />
    <path d="M6 20h36" stroke="currentColor" strokeWidth="2" />
    <rect x="10" y="28" width="12" height="4" rx="1" fill="currentColor" opacity="0.35" />
  </svg>
);

const PaymentCardIcon = () => (
  <svg className="manage-drawer-price-tab-icon" viewBox="0 0 16 16" fill="none" aria-hidden>
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" fill="currentColor" opacity="0.2" />
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1" />
    <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1" />
    <rect x="3.5" y="9" width="4" height="1.5" rx="0.5" fill="currentColor" opacity="0.5" />
  </svg>
);

const PriceBreakdownTabIcon = () => (
  <svg className="manage-drawer-price-tab-icon" viewBox="0 0 16 16" fill="none" aria-hidden>
    <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M5 5.5h6M5 8h6M5 10.5h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const getOnlinePaymentOptionCopy = (mode) => {
  if (mode === 'external_link') {
    return {
      showStripeLogo: false,
      title: 'Request via payment link',
      recommended: false,
      description: 'Sends your venue payment link to the organiser.'
    };
  }
  if (mode === 'invoice') {
    return {
      showStripeLogo: false,
      title: 'Send payment instructions',
      recommended: false,
      description:
        'Sends an email with a link to view the booking and pay using your uploaded invoice.'
    };
  }
  return {
    showStripeLogo: true,
    title: 'Request via',
    recommended: true,
    description: 'Sends a payment link. Status updates on its own once paid.'
  };
};
const MAX_MESSAGE_LENGTH = 100;

/** Notes list row: "15:59, 4 May" */
const formatNotesListTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const day = date.getDate();
  const month = date.toLocaleDateString([], { month: 'short' });
  return `${time}, ${day} ${month}`;
};

const parseMoney = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim().toUpperCase();
  if (s === 'TBC' || s === 'N/A') return null;
  const n = Number.parseFloat(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const formatUnitCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(Number.isFinite(Number(amount)) ? Number(amount) : 0);

const toDateInputValue = (value) => {
  if (!value) return '';
  const str = String(value);
  if (str.includes('T')) return str.split('T')[0];
  return str;
};

const toTimeInputValue = (value) => {
  if (!value) return '';
  const str = String(value);
  const [hh = '', mm = ''] = str.split(':');
  if (!hh) return '';
  return `${hh.padStart(2, '0')}:${(mm || '00').padStart(2, '0')}`;
};

const toNumericInputValue = (value, fallback = 0) => {
  const parsed = parseMoney(value);
  if (parsed == null) return String(fallback);
  return String(parsed);
};

const getMinEndTime = (startTime) => {
  if (!startTime) return null;
  const [hh = '00', mm = '00'] = String(startTime).split(':');
  const mins = (Number(hh) || 0) * 60 + (Number(mm) || 0) + 30;
  const wrapped = ((mins % (24 * 60)) + (24 * 60)) % (24 * 60);
  const nextH = String(Math.floor(wrapped / 60)).padStart(2, '0');
  const nextM = String(wrapped % 60).padStart(2, '0');
  return `${nextH}:${nextM}`;
};

/**
 * Normalizes booking line items (API `packages` or requirements.packages) for the add-ons editor.
 */
const extractPackagesFromBooking = (booking) => {
  if (!booking) return [];
  const raw = getBookingPackageLines(booking);
  if (!Array.isArray(raw)) return [];
  return raw.map((p, idx) => {
    const packageId = p.package_id ?? p.id ?? p.item_id ?? null;
    const name = p.name ?? p.package_name ?? p.title ?? 'Add-on';
    const qtyRaw = p.quantity ?? p.qty;
    const qty = qtyRaw == null || qtyRaw === '' ? 1 : Math.max(0, Number(qtyRaw) || 0);
    const unitFrom = parseMoney(p.unit_price ?? p.unitPrice ?? p.price_per_unit);
    const totalRaw = parseMoney(p.total ?? p.amount);
    let total = totalRaw;
    let unit = unitFrom;

    // When both unit and total are present but disagree (common in historical payloads),
    // trust total because subtotal and persisted amount are based on it.
    if (total != null) {
      if (qty > 0) unit = roundMoney(total / qty);
    } else if (unit != null) {
      total = roundMoney(unit * qty);
    }

    if (unit == null) unit = 0;
    if (total == null) total = roundMoney(unit * qty);
    return {
      _key: `${packageId ?? 'row'}-${idx}`,
      package_id: packageId,
      name,
      quantity: qty,
      unit_price: roundMoney(unit),
      total: roundMoney(total)
    };
  });
};

const formatBookingTypeLabel = (type) =>
  type === 'full-venue' ? 'Full venue hire' : 'Per person';

const normalizePackageLinesForCompare = (lines) =>
  (lines || [])
    .filter((line) => (line.quantity ?? 0) > 0)
    .map((line) => ({
      package_id: line.package_id ?? null,
      name: line.name ?? 'Add-on',
      quantity: line.quantity ?? 0,
      total: roundMoney(line.total ?? 0)
    }))
    .sort((a, b) => `${a.package_id}-${a.name}`.localeCompare(`${b.package_id}-${b.name}`));

const packagesEqual = (left, right) => {
  const a = normalizePackageLinesForCompare(left);
  const b = normalizePackageLinesForCompare(right);
  if (a.length !== b.length) return false;
  return a.every(
    (line, index) =>
      line.package_id === b[index].package_id &&
      line.name === b[index].name &&
      line.quantity === b[index].quantity &&
      line.total === b[index].total
  );
};

const formatPackageLinesSummary = (lines) => {
  const active = normalizePackageLinesForCompare(lines);
  if (active.length === 0) return 'None';
  return active
    .map((line) => `${line.name} ×${line.quantity} (${formatCurrency(line.total)})`)
    .join(', ');
};

const pushDraftChange = (changes, label, before, after) => {
  const beforeStr = before == null || before === '' ? '—' : String(before);
  const afterStr = after == null || after === '' ? '—' : String(after);
  if (beforeStr !== afterStr) {
    changes.push({ label, before: beforeStr, after: afterStr });
  }
};

const pushDraftMoneyChange = (changes, label, before, after) => {
  const beforeMoney = parseMoney(before);
  const afterMoney = parseMoney(after);
  if (beforeMoney == null && afterMoney == null) return;
  if (beforeMoney != null && afterMoney != null && roundMoney(beforeMoney) === roundMoney(afterMoney)) {
    return;
  }
  pushDraftChange(changes, label, formatCurrency(beforeMoney), formatCurrency(afterMoney));
};

/**
 * Hire amount when the drawer opens. Keep the stored space fee whenever one exists
 * (including paid bookings) so the drawer matches the card and the charged total.
 * Formula-fill only for TBC / unpriced future bookings. Pending is derived from
 * that same hire so opening an unedited booking is not treated as unsaved changes.
 */
const resolveOpenedBookingPricing = (sourceBooking, { venueId } = {}) => {
  const bookingReq = parseBookingRequirements(sourceBooking);
  const amounts = resolveBookingAmounts(sourceBooking, {
    venueId: sourceBooking.venue_id ?? sourceBooking.venueId ?? venueId
  });
  const awaitingPricing = needsVenuePricingConfirmation(sourceBooking);
  const snapshot = parseLastPricedSnapshot(bookingReq);
  const isPaid = ['completed', 'paid', '1'].includes(
    String(sourceBooking.payment_status ?? sourceBooking.paymentStatus ?? '').toLowerCase()
  );
  const stuckFreeFlag = false;
  const snapshotDraft = null;

  const restoredBaseAmount =
    parseMoney(bookingReq.base_fee) ??
    (parseMoney(bookingReq.booking_amount) != null && parseMoney(bookingReq.add_ons_total) != null
      ? roundMoney(parseMoney(bookingReq.booking_amount) - parseMoney(bookingReq.add_ons_total))
      : parseMoney(bookingReq.booking_amount)) ??
    amounts.baseAmount;
  const restoredAddOnsTotal = parseMoney(bookingReq.add_ons_total) ?? amounts.addOnsTotal;
  const pricingMetaForDraft = readPricingMetadata(sourceBooking, bookingReq);
  const draftDurationHours =
    calculateDurationHours(
      toTimeInputValue(sourceBooking.start_time),
      toTimeInputValue(sourceBooking.end_time)
    ) ||
    parseMoney(bookingReq.duration_hours ?? sourceBooking.duration_hours) ||
    0;
  const draftHeadcount = bookingHeadcount(sourceBooking, bookingReq);
  const hasStoredSpaceFee = (restoredBaseAmount ?? 0) > 0;
  const canFormulaFill =
    !hasStoredSpaceFee &&
    !isPaid &&
    !isPastBookingDate(sourceBooking) &&
    !awaitingPricing &&
    !stuckFreeFlag &&
    snapshotDraft?.bookingAmount == null &&
    draftDurationHours > 0 &&
    ((pricingMetaForDraft.pricingMode === 'per_person' &&
      pricingMetaForDraft.perPersonRate != null &&
      Number.isFinite(draftHeadcount) &&
      draftHeadcount > 0) ||
      (pricingMetaForDraft.pricingMode === 'exclusive' &&
        pricingMetaForDraft.spaceHourlyRate != null));
  const formulaSpaceFee = canFormulaFill
    ? computeBaseFromPricingMetadata({
        pricingMode: pricingMetaForDraft.pricingMode,
        headcount: draftHeadcount,
        duration: draftDurationHours,
        perPersonRate: pricingMetaForDraft.perPersonRate,
        spaceHourlyRate: pricingMetaForDraft.spaceHourlyRate
      })
    : null;
  const resolvedBaseAmount =
    snapshotDraft?.bookingAmount != null
      ? parseMoney(snapshotDraft.bookingAmount)
      : stuckFreeFlag || hasStoredSpaceFee
        ? restoredBaseAmount
        : formulaSpaceFee ?? amounts.baseAmount;
  const resolvedAddOnsTotal =
    snapshotDraft?.addOnsAmount != null
      ? parseMoney(snapshotDraft.addOnsAmount)
      : stuckFreeFlag
        ? restoredAddOnsTotal
        : amounts.addOnsTotal;
  const resolvedPaid =
    snapshotDraft?.amountPaid != null ? parseMoney(snapshotDraft.amountPaid) : amounts.amountPaid;
  const discountAmount =
    parseMoney(sourceBooking.discount_amount ?? bookingReq.discount_amount) ?? 0;
  const freeBookingMode = resolveFreeBookingMode(sourceBooking);

  const storedPending = parseMoney(
    sourceBooking.amount_pending ?? bookingReq.amount_pending
  );
  let organizerPending;
  if (snapshotDraft?.amountPending != null) {
    organizerPending = parseMoney(snapshotDraft.amountPending);
  } else if (isPaid) {
    organizerPending = 0;
  } else if (storedPending != null) {
    organizerPending = storedPending;
  } else {
    // Do not invent pending from add-ons / fees on open — that looks like an unsaved edit.
    organizerPending = 0;
  }

  return {
    bookingReq,
    amounts,
    awaitingPricing,
    stuckFreeFlag,
    snapshot,
    snapshotDraft,
    restoredBaseAmount,
    restoredAddOnsTotal,
    resolvedBaseAmount,
    resolvedAddOnsTotal,
    resolvedPaid,
    organizerPending,
    freeBookingMode,
    discountAmount,
    packages: extractPackagesFromBooking(sourceBooking)
  };
};

const formatDraftEventDate = (value) => {
  if (!value) return '—';
  const parsed = parseDateOnly(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return String(value);
  return formatDate(parsed, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Compare draft state to the stored booking and return human-readable pending edits.
 */
const buildUnsavedDraftChangeSummary = (
  booking,
  detailsDraft,
  draftPackages,
  {
    venueId,
    pendingManuallyEdited = false,
    bookingAmountManuallyEdited = false,
    amountPaidManuallyEdited = false
  } = {}
) => {
  if (!booking) return [];

  const req = parseBookingRequirements(booking);
  const changes = [];
  const storedTitle = String(booking.event_name ?? booking.event ?? booking.eventName ?? '').trim();
  const draftTitle = String(detailsDraft.eventTitle ?? '').trim();
  pushDraftChange(changes, 'Event name', storedTitle || '—', draftTitle || '—');

  pushDraftChange(
    changes,
    'Event date',
    formatDraftEventDate(toDateInputValue(booking.booking_date)),
    formatDraftEventDate(detailsDraft.eventDate)
  );
  pushDraftChange(
    changes,
    'Start time',
    booking.start_time ? formatTime(booking.start_time) : '—',
    detailsDraft.eventStartTime ? formatTime(detailsDraft.eventStartTime) : '—'
  );
  pushDraftChange(
    changes,
    'End time',
    booking.end_time ? formatTime(booking.end_time) : '—',
    detailsDraft.eventEndTime ? formatTime(detailsDraft.eventEndTime) : '—'
  );

  const storedType = resolveBookingTypeFromRecord(booking, req);
  pushDraftChange(
    changes,
    'Pricing mode',
    formatBookingTypeLabel(storedType),
    formatBookingTypeLabel(detailsDraft.bookingType || storedType)
  );

  pushDraftChange(
    changes,
    'Event activity',
    String(req.activity ?? booking.activity ?? booking.event_type ?? '').trim() || '—',
    String(detailsDraft.activity ?? '').trim() || '—'
  );

  const storedAttendees = String(bookingHeadcount(booking, req) ?? '').trim();
  pushDraftChange(changes, 'Guests', storedAttendees || '—', String(detailsDraft.attendees ?? '').trim() || '—');

  pushDraftChange(
    changes,
    'Catering',
    String(req.catering_preference ?? booking.catering ?? '').trim() || '—',
    String(detailsDraft.catering ?? '').trim() || '—'
  );
  pushDraftChange(
    changes,
    'Dietary requirements',
    formatBookingDietaryRestrictions({
      requirements: req,
      catering_preference: req.catering_preference ?? booking.catering,
      has_dietary_restrictions: req.has_dietary_restrictions,
      dietary_restrictions: req.dietary_restrictions
    }) || '—',
    formatBookingDietaryRestrictions({
      requirements: {
        catering_preference: detailsDraft.catering,
        has_dietary_restrictions: detailsDraft.hasDietaryRestrictions,
        dietary_restrictions: detailsDraft.dietaryRestrictions
      },
      catering_preference: detailsDraft.catering,
      has_dietary_restrictions: detailsDraft.hasDietaryRestrictions,
      dietary_restrictions: detailsDraft.dietaryRestrictions
    }) || '—'
  );
  pushDraftChange(
    changes,
    'Audio/visual (A/V)',
    formatBookingAv({
      requirements: req,
      needs_av: req.needs_av
    }),
    detailsDraft.needsAv ? 'Yes' : 'No'
  );
  pushDraftChange(
    changes,
    'Alcohol',
    String(req.alcohol_consumption ?? booking.alcohol ?? '').trim() || '—',
    String(detailsDraft.alcohol ?? '').trim() || '—'
  );

  const openedPricing = resolveOpenedBookingPricing(booking, {
    venueId: venueId ?? booking.venue_id ?? booking.venueId
  });
  const paymentFields = resolveBookingPaymentDisplayFields(booking);
  const draftBookingAmount = parseMoney(detailsDraft.bookingAmount);
  const draftAmountPaid = parseMoney(detailsDraft.amountPaid);
  const draftAmountPending = parseMoney(detailsDraft.amountPending);

  if (bookingAmountManuallyEdited && draftBookingAmount != null) {
    pushDraftMoneyChange(
      changes,
      'Booking amount',
      openedPricing.resolvedBaseAmount,
      draftBookingAmount
    );
  }
  if (amountPaidManuallyEdited && draftAmountPaid != null) {
    pushDraftMoneyChange(changes, 'Amount paid', openedPricing.resolvedPaid, draftAmountPaid);
  }
  if (pendingManuallyEdited && draftAmountPending != null) {
    pushDraftMoneyChange(
      changes,
      'Amount pending',
      openedPricing.organizerPending,
      draftAmountPending
    );
  }

  const storedDiscountAmount = Number.parseFloat(
    booking.discount_amount ?? req.discount_amount ?? 0
  );
  const draftDiscountAmount = parseMoney(detailsDraft.discountAmount);
  if (draftDiscountAmount != null) {
    pushDraftMoneyChange(
      changes,
      'Discount',
      Number.isFinite(storedDiscountAmount) ? storedDiscountAmount : 0,
      draftDiscountAmount
    );
  }
  pushDraftChange(
    changes,
    'Discount code',
    String(booking.discount_code ?? req.discount_code ?? '').trim() || '—',
    String(detailsDraft.discountCode ?? '').trim() || '—'
  );

  pushDraftChange(
    changes,
    'Transaction ID',
    paymentFields.transactionId || '—',
    detailsDraft.transactionId?.trim() || '—'
  );
  pushDraftChange(
    changes,
    'Payment method',
    paymentFields.paymentMethod || '—',
    detailsDraft.paymentMethod?.trim() || '—'
  );

  pushDraftChange(
    changes,
    'Pricing waiver',
    formatFreeBookingModeLabel(openedPricing.freeBookingMode),
    formatFreeBookingModeLabel(detailsDraft.freeBookingMode)
  );

  const storedPackages = extractPackagesFromBooking(booking);
  if (!packagesEqual(storedPackages, draftPackages)) {
    pushDraftChange(
      changes,
      'Add-ons',
      formatPackageLinesSummary(storedPackages),
      formatPackageLinesSummary(draftPackages)
    );
  }

  return changes;
};

const getUserId = (currentUser, userProfile) => getBackendUserId(currentUser, userProfile);

const extractThreadId = (threadPayload) =>
  threadPayload?.thread_id ||
  threadPayload?.id ||
  threadPayload?.data?.thread_id ||
  threadPayload?.data?.id ||
  null;

const getInvoiceMetaFromBooking = (booking) => {
  const req = booking?.requirements || {};
  const invoiceCandidate =
    booking?.invoice ||
    req?.invoice ||
    booking?.invoice_file ||
    req?.invoice_file ||
    null;

  const url =
    booking?.invoice_url ||
    req?.invoice_url ||
    invoiceCandidate?.url ||
    invoiceCandidate?.file_url ||
    null;

  const name =
    booking?.invoice_file_name ||
    req?.invoice_file_name ||
    invoiceCandidate?.name ||
    invoiceCandidate?.file_name ||
    (typeof url === 'string' ? url.split('/').pop() : null) ||
    null;

  if (!url && !name) return null;
  return { name: name || 'Invoice.pdf', url: url || null };
};

const getInvoiceDisplayName = (invoice) => {
  if (!invoice) return 'Invoice.pdf';

  const directName =
    invoice.filename ||
    invoice.original_filename ||
    invoice.originalFilename ||
    invoice.file_name ||
    invoice.fileName ||
    invoice.display_name ||
    invoice.displayName ||
    invoice.name ||
    null;

  let resolvedName = typeof directName === 'string' && directName.trim() ? directName.trim() : null;

  if (!resolvedName) {
    const key = invoice.s3_key || invoice.s3Key || null;
    if (typeof key === 'string' && key.trim()) {
      resolvedName = key.split('/').pop() || null;
    }
  }

  if (!resolvedName) {
    const url = invoice.url || invoice.file_url || invoice.fileUrl || null;
    if (typeof url === 'string' && url.trim()) {
      resolvedName = (url.split('/').pop() || '').split('?')[0] || null;
    }
  }

  if (!resolvedName) return 'Invoice.pdf';
  try {
    return decodeURIComponent(resolvedName);
  } catch (e) {
    return resolvedName;
  }
};

const ManageBookingDrawer = ({
  booking,
  venueId,
  venuePaymentLink = '',
  bookingRules = null,
  onClose,
  onUpdateBookingStatus,
  onBookingRefresh,
  onBookingLocalUpdate,
  onOpenMessages,
  initialTab = TAB_BOOKING,
  initialPriceTab = null,
  openCancelOnMount = false,
  autoStartMessage = false,
  mobileSection = null,
  onMobileBack = null
}) => {
  const { currentUser, userProfile } = useAuth();
  const [modalTab, setModalTab] = useState(initialTab || TAB_BOOKING);
  const drawerNavigationIntentRef = useRef('close');
  const [, setBookingStatusAction] = useState('');
  const [paymentStatusAction, setPaymentStatusAction] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderError, setReminderError] = useState('');
  const [reminderSuccess, setReminderSuccess] = useState('');
  const [feedbackMeta, setFeedbackMeta] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [sendingFeedbackRequest, setSendingFeedbackRequest] = useState(false);
  const [feedbackRequestError, setFeedbackRequestError] = useState('');
  const [feedbackRequestSuccess, setFeedbackRequestSuccess] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const invoiceInputRef = useRef(null);
  /** Preserves pricing when toggling free ↔ paid before save (unpaid bookings only). */
  const freeTogglePricingSnapshotRef = useRef(null);
  const autoMessageStartedRef = useRef(false);

  const [bookingNotes, setBookingNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [newNoteBody, setNewNoteBody] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteBody, setEditingNoteBody] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const [auditLogEntries, setAuditLogEntries] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogError, setAuditLogError] = useState('');

  const [draftPackages, setDraftPackages] = useState([]);
  const [addonsDirty, setAddonsDirty] = useState(false);
  const [venueAddonCatalog, setVenueAddonCatalog] = useState([]);
  const [addonsCatalogVenueId, setAddonsCatalogVenueId] = useState(null);
  const [addonsCatalogLoading, setAddonsCatalogLoading] = useState(false);
  const [addonsSaveError, setAddonsSaveError] = useState('');
  const [addonsSaving, setAddonsSaving] = useState(false);
  const [addonPickerOpen, setAddonPickerOpen] = useState(false);
  const [addonPickerSelection, setAddonPickerSelection] = useState('');
  const [venueOwnerUserId, setVenueOwnerUserId] = useState(null);
  const [detailsDraft, setDetailsDraft] = useState({
    eventDate: '',
    eventStartTime: '',
    eventEndTime: '',
    bookingType: 'table',
    eventTitle: '',
    activity: '',
    attendees: '',
    catering: '',
    hasDietaryRestrictions: false,
    dietaryRestrictions: '',
    needsAv: false,
    alcohol: '',
    transactionId: '',
    paymentMethod: '',
    paymentDate: '',
    bookingAmount: '0',
    amountPaid: '0',
    amountPending: '0',
    addOnsAmount: '0',
    discountCode: '',
    discountAmount: '0',
    freeBookingMode: FREE_BOOKING_MODE.NONE
  });
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [detailsSaveError, setDetailsSaveError] = useState('');
  const [approvalFeedback, setApprovalFeedback] = useState('');
  const [isDetailsEditMode, setIsDetailsEditMode] = useState(false);
  const [isPaymentEditMode, setIsPaymentEditMode] = useState(false);
  const [pendingManuallyEdited, setPendingManuallyEdited] = useState(false);
  const [bookingAmountManuallyEdited, setBookingAmountManuallyEdited] = useState(false);
  const [amountPaidManuallyEdited, setAmountPaidManuallyEdited] = useState(false);
  const [venueTimezone, setVenueTimezone] = useState(null);
  const [pricingRecalcVersion, setPricingRecalcVersion] = useState(0);
  const [pricingRecalcTrigger, setPricingRecalcTrigger] = useState(null);
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  const [closePromptBusy, setClosePromptBusy] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [priceDetailsTab, setPriceDetailsTab] = useState(initialPriceTab || PRICE_TAB_BREAKDOWN);
  const [isPriceEditMode, setIsPriceEditMode] = useState(false);
  const [paymentCollectionMethod, setPaymentCollectionMethod] = useState('online');
  const [paymentContext, setPaymentContext] = useState(null);
  const [paymentContextLoading, setPaymentContextLoading] = useState(false);
  const [markAsPaidFlow, setMarkAsPaidFlow] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [discountValidating, setDiscountValidating] = useState(false);
  const [openingMessageThread, setOpeningMessageThread] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const req = useMemo(() => parseBookingRequirements(booking), [booking]);
  const isPastBooking = useMemo(
    () => Boolean(booking && isPastBookingDate(booking)),
    [booking]
  );
  const isBookingReadOnly = isPastBooking;
  const organizerContact = useMemo(
    () => resolveBookingOrganizerContact(booking, bookingNotes[0]),
    [booking, bookingNotes]
  );
  const recurringInfo = getBookingRecurringInfo(booking);
  const ownerUserId = getUserId(currentUser, userProfile);
  const ownerInvoiceActor = ownerUserId || userProfile?.email || currentUser?.email || null;
  const notesOwnerActor = ownerInvoiceActor;
  const bookingIdResolved = booking?.id ?? booking?.booking_reference ?? null;
  const bookingVenueId = venueId || booking?.venue_id || booking?.venueId || null;

  useEffect(() => {
    if (!bookingVenueId) return;
    venueDashboardApiService.getVenueDetails(bookingVenueId).catch(() => {});
  }, [bookingVenueId]);

  const guestOrganizerEmail = organizerContact.email;
  const organizerUserId = booking?.customer_id || req.customer_info?.id || null;
  const organizerReceiverId = guestOrganizerEmail || organizerUserId || null;
  const isAdmin = isAdminUser(userProfile);
  const adminOrganizerProfilePath =
    isAdmin && (organizerUserId || guestOrganizerEmail)
      ? buildAdminOrganizerProfilePath({
          userId: organizerUserId || undefined,
          email: !organizerUserId ? guestOrganizerEmail : undefined
        })
      : null;
  const adminOrganizerBookingPath =
    isAdmin && bookingIdResolved && (organizerUserId || guestOrganizerEmail)
      ? buildAdminBookingViewPath(bookingIdResolved, {
          userId: organizerUserId || undefined,
          email: !organizerUserId ? guestOrganizerEmail : undefined
        })
      : null;
  const messageSenderId = venueOwnerUserId || ownerUserId;

  useEffect(() => {
    if (!bookingVenueId) {
      setVenueOwnerUserId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const venue = await venueDashboardApiService.getVenueDetails(bookingVenueId);
        const resolvedOwnerId = venue?.owner_id || venue?.owner?.id || null;
        if (!cancelled) {
          setVenueOwnerUserId(resolvedOwnerId);
          setVenueTimezone(venue?.timezone || venue?.time_zone || null);
        }
      } catch {
        if (!cancelled) setVenueOwnerUserId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingVenueId]);

  const fallbackInvoiceMeta = getInvoiceMetaFromBooking(booking);
  const readyInvoices = invoiceFiles.filter((file) => (file.status || '').toLowerCase() === 'ready');
  const activeInvoice = readyInvoices[0] || invoiceFiles[0] || fallbackInvoiceMeta;

  const loadInvoiceFiles = async () => {
    if (!bookingIdResolved || !ownerInvoiceActor) return;
    try {
      setInvoiceLoading(true);
      setInvoiceError('');
      const files = await venueDashboardApiService.listBookingOwnerInvoices(bookingIdResolved, {
        ownerId: ownerInvoiceActor
      });
      setInvoiceFiles(files);
    } catch (error) {
      setInvoiceError(error?.message || 'Failed to load invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  useEffect(() => {
    loadInvoiceFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadInvoiceFiles is recreated per render; bookingIdResolved is the sync key
  }, [bookingIdResolved, ownerInvoiceActor]);

  useEffect(() => {
    if (!bookingIdResolved) {
      setFeedbackMeta(null);
      return;
    }
    let cancelled = false;
    setFeedbackLoading(true);
    venueDashboardApiService
      .getBookingFeedback(bookingIdResolved)
      .then((data) => {
        if (!cancelled) setFeedbackMeta(data);
      })
      .catch(() => {
        if (!cancelled) setFeedbackMeta(null);
      })
      .finally(() => {
        if (!cancelled) setFeedbackLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingIdResolved]);

  const applyBookingToDraft = (sourceBooking) => {
    if (!sourceBooking) {
      setDraftPackages([]);
      return;
    }
    setAddonsDirty(false);
    setAddonsSaveError('');
    const {
      bookingReq,
      amounts,
      stuckFreeFlag,
      snapshotDraft,
      restoredBaseAmount,
      restoredAddOnsTotal,
      resolvedBaseAmount,
      resolvedAddOnsTotal,
      resolvedPaid,
      organizerPending,
      freeBookingMode,
      packages
    } = resolveOpenedBookingPricing(sourceBooking, {
      venueId: sourceBooking.venue_id ?? sourceBooking.venueId ?? venueId
    });
    const paymentFields = resolveBookingPaymentDisplayFields(sourceBooking);
    let packagesForDraft = packages;
    if (stuckFreeFlag) {
      if (snapshotDraft?.draftPackages?.length) {
        packagesForDraft = snapshotDraft.draftPackages;
      } else if (Array.isArray(bookingReq.packages) && bookingReq.packages.length) {
        packagesForDraft = bookingReq.packages.map((line, idx) => ({
          _key: `${line.package_id ?? line.id ?? 'row'}-${idx}`,
          package_id: line.package_id ?? line.id ?? null,
          name: line.name ?? line.package_name ?? line.title ?? 'Add-on',
          quantity: line.quantity ?? line.qty ?? 1,
          unit_price: roundMoney(parseMoney(line.unit_price ?? line.unitPrice ?? 0) ?? 0),
          total: roundMoney(parseMoney(line.total ?? line.amount ?? 0) ?? 0)
        }));
      }
    }
    setDraftPackages(packagesForDraft);
    setDetailsDraft({
      eventDate: toDateInputValue(sourceBooking.booking_date),
      eventStartTime: toTimeInputValue(sourceBooking.start_time),
      eventEndTime: toTimeInputValue(sourceBooking.end_time),
      bookingType: resolveBookingTypeFromRecord(sourceBooking, bookingReq),
      eventTitle:
        sourceBooking.event_name ??
        sourceBooking.event ??
        sourceBooking.eventName ??
        '',
      activity: bookingReq.activity ?? sourceBooking.activity ?? sourceBooking.event_type ?? '',
      attendees: String(bookingHeadcount(sourceBooking, bookingReq) ?? ''),
      catering: bookingReq.catering_preference ?? sourceBooking.catering ?? '',
      hasDietaryRestrictions: Boolean(bookingReq.has_dietary_restrictions),
      dietaryRestrictions: bookingReq.dietary_restrictions ?? '',
      needsAv: bookingNeedsAv({ requirements: bookingReq, needs_av: sourceBooking.needs_av }),
      alcohol: bookingReq.alcohol_consumption ?? sourceBooking.alcohol ?? '',
      transactionId: paymentFields.transactionId,
      paymentMethod: paymentFields.paymentMethod,
      paymentDate: toDateInputValue(bookingReq.payment_date ?? sourceBooking.payment_date),
      bookingAmount: toNumericInputValue(
        snapshotDraft?.bookingAmount != null
          ? parseMoney(snapshotDraft.bookingAmount)
          : stuckFreeFlag
            ? restoredBaseAmount
            : resolvedBaseAmount
      ),
      amountPaid: toNumericInputValue(
        snapshotDraft?.amountPaid != null ? parseMoney(snapshotDraft.amountPaid) : resolvedPaid
      ),
      amountPending: toNumericInputValue(
        snapshotDraft?.amountPending != null
          ? parseMoney(snapshotDraft.amountPending)
          : organizerPending
      ),
      addOnsAmount: toNumericInputValue(
        snapshotDraft?.addOnsAmount != null
          ? parseMoney(snapshotDraft.addOnsAmount)
          : stuckFreeFlag
            ? restoredAddOnsTotal
            : resolvedAddOnsTotal
      ),
      discountCode: String(booking.discount_code ?? bookingReq.discount_code ?? '').trim(),
      discountAmount: toNumericInputValue(
        Number.parseFloat(booking.discount_amount ?? bookingReq.discount_amount ?? 0) || 0
      ),
      freeBookingMode
    });
    freeTogglePricingSnapshotRef.current = resolveInitialPricingRef({
      requirements: bookingReq,
      booking: sourceBooking,
      packages,
      amounts
    });
    setDetailsDirty(false);
    setDetailsSaveError('');
    setPendingManuallyEdited(false);
    setBookingAmountManuallyEdited(false);
    setAmountPaidManuallyEdited(false);
    setPricingRecalcVersion(0);
    setPricingRecalcTrigger(null);
  };

  useEffect(() => {
    if (!booking) {
      setDraftPackages([]);
      return;
    }
    setModalTab(initialTab || TAB_BOOKING);
    if (initialPriceTab) setPriceDetailsTab(initialPriceTab);
    if (openCancelOnMount) setCancelConfirmOpen(true);
    applyBookingToDraft(booking);
    setApprovalFeedback('');
    setVenueAddonCatalog([]);
    setAddonsCatalogVenueId(null);
    setBookingNotes([]);
    setNotesError('');
    setNewNoteBody('');
    setEditingNoteId(null);
    setEditingNoteBody('');
    setAddonPickerOpen(false);
    setAddonPickerSelection('');
    setPaymentStatusAction('');
    setIsDetailsEditMode(false);
    setIsPriceEditMode(false);
    setIsPaymentEditMode(
      !isPastBookingDate(booking) && shouldStartInPaymentEditMode(booking)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset drawer when booking identity changes only
  }, [booking?.id, booking?.booking_reference]);

  useEffect(() => {
    if (mobileSection?.key !== 'requirements' || !booking || isPastBookingDate(booking)) return;
    setIsDetailsEditMode(true);
  }, [mobileSection?.key, booking]);

  const userEditedDraftRef = useRef(false);
  userEditedDraftRef.current =
    detailsDirty || addonsDirty || bookingAmountManuallyEdited || pendingManuallyEdited;

  useEffect(() => {
    if (!booking) return;
    if (userEditedDraftRef.current) return;
    applyBookingToDraft(booking);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync draft when payment/status fields change
  }, [
    booking?.payment_status,
    booking?.paymentStatus,
    booking?.total_amount,
    booking?.amount_pending,
    booking?.final_amount,
    booking?.status,
    booking?.booking_status
  ]);

  const venueIdForAddons = booking?.venue_id || booking?.venueId;

  const loadBookingNotes = useCallback(async () => {
    if (!bookingIdResolved || !notesOwnerActor) return;
    setNotesLoading(true);
    setNotesError('');
    try {
      const notes = await venueDashboardApiService.listBookingNotes(bookingIdResolved, {
        ownerId: notesOwnerActor
      });
      setBookingNotes(Array.isArray(notes) ? notes : []);
    } catch (e) {
      setNotesError(e?.message || 'Failed to load notes');
    } finally {
      setNotesLoading(false);
    }
  }, [bookingIdResolved, notesOwnerActor]);

  useEffect(() => {
    if (modalTab !== TAB_NOTES && organizerContact.email) return;
    loadBookingNotes();
  }, [modalTab, loadBookingNotes, organizerContact.email]);

  const loadBookingAuditLog = useCallback(async () => {
    if (!bookingIdResolved) return;
    setAuditLogLoading(true);
    setAuditLogError('');
    try {
      const entries = await venueDashboardApiService.getBookingAuditLog(bookingIdResolved);
      setAuditLogEntries(Array.isArray(entries) ? entries : []);
    } catch (e) {
      // Audit log API may not be deployed yet — don't block the drawer with a 404.
      const message = e?.message || '';
      if (message.includes('not found') || message.includes('404')) {
        setAuditLogEntries([]);
        setAuditLogError('');
      } else {
        setAuditLogError(message || 'Failed to load change log');
        setAuditLogEntries([]);
      }
    } finally {
      setAuditLogLoading(false);
    }
  }, [bookingIdResolved]);

  useEffect(() => {
    if (modalTab !== TAB_CHANGELOG) return;
    loadBookingAuditLog();
  }, [modalTab, loadBookingAuditLog]);

  const canEditBookingNote = (note) => {
    const source = note?.source || '';
    return source === 'venue_internal' || source === 'manual_dashboard';
  };

  const handleAddNote = async () => {
    if (isBookingReadOnly || !bookingIdResolved || !notesOwnerActor || !newNoteBody.trim() || notesSaving) return;
    setNotesSaving(true);
    setNotesError('');
    try {
      await venueDashboardApiService.createBookingNote(bookingIdResolved, newNoteBody, {
        ownerId: notesOwnerActor,
        source: 'venue_internal'
      });
      setNewNoteBody('');
      await loadBookingNotes();
    } catch (e) {
      setNotesError(e?.message || 'Failed to add note');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteBody(note.body || '');
    setNotesError('');
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteBody('');
  };

  const handleSaveEditNote = async (noteId) => {
    if (!bookingIdResolved || !notesOwnerActor || !editingNoteBody.trim() || notesSaving) return;
    setNotesSaving(true);
    setNotesError('');
    try {
      await venueDashboardApiService.updateBookingNote(bookingIdResolved, noteId, editingNoteBody, {
        ownerId: notesOwnerActor
      });
      setEditingNoteId(null);
      setEditingNoteBody('');
      await loadBookingNotes();
    } catch (e) {
      setNotesError(e?.message || 'Failed to save note');
    } finally {
      setNotesSaving(false);
    }
  };

  useEffect(() => {
    if (modalTab !== TAB_ADDONS) {
      setAddonPickerOpen(false);
      setAddonPickerSelection('');
    }
  }, [modalTab]);

  useEffect(() => {
    if (modalTab !== TAB_ADDONS || !venueIdForAddons) return;
    let cancelled = false;
    (async () => {
      setAddonsCatalogLoading(true);
      setAddonsCatalogVenueId(null);
      try {
        const [packageResult, addonResult, otherResult] = await Promise.allSettled([
          venueDashboardApiService.getVenuePackages(venueIdForAddons),
          venueDashboardApiService.getVenueAddOnItems(venueIdForAddons, { itemKind: 'addon' }),
          venueDashboardApiService.getVenueAddOnItems(venueIdForAddons, { itemKind: 'other' })
        ]);
        const packages = (
          packageResult.status === 'fulfilled' && Array.isArray(packageResult.value)
            ? packageResult.value
            : []
        ).map((item) => ({
          ...item,
          item_kind: item.item_kind || 'package'
        }));
        const addonsRaw = addonResult.status === 'fulfilled' ? addonResult.value : [];
        const otherRaw = otherResult.status === 'fulfilled' ? otherResult.value : [];
        const addonItems = Array.isArray(addonsRaw) ? addonsRaw : addonsRaw?.items ?? [];
        const otherItems = Array.isArray(otherRaw) ? otherRaw : otherRaw?.items ?? [];
        const list = [...packages, ...addonItems, ...otherItems];
        if (!cancelled) {
          if (list.length > 0 || venueAddonCatalog.length === 0) {
            setVenueAddonCatalog(list);
          }
          setAddonsCatalogVenueId(venueIdForAddons);
        }
      } catch {
        if (!cancelled) {
          setAddonsCatalogVenueId(venueIdForAddons);
        }
      } finally {
        if (!cancelled) setAddonsCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalTab, venueIdForAddons, venueAddonCatalog.length]);

  const addonsCatalogReady =
    Boolean(venueIdForAddons) &&
    addonsCatalogVenueId === venueIdForAddons &&
    !addonsCatalogLoading;

  const orphanAddonRows = useMemo(
    () =>
      draftPackages.filter((p) => {
        if ((p.quantity ?? 0) <= 0) return false;
        // Invoice / custom lines have no catalog package_id — still show them in the editor.
        if (p.package_id == null) return true;
        return !venueAddonCatalog.some(
          (x) => String(x.id || x.item_id) === String(p.package_id)
        );
      }),
    [draftPackages, venueAddonCatalog]
  );
  const activeCatalogAddonRows = useMemo(
    () =>
      venueAddonCatalog.filter((item) => {
        const id = item.id || item.item_id;
        const draftLine = draftPackages.find((p) => String(p.package_id) === String(id));
        return (draftLine?.quantity ?? 0) > 0;
      }),
    [venueAddonCatalog, draftPackages]
  );
  const availableCatalogForPicker = useMemo(
    () =>
      venueAddonCatalog.filter((item) => {
        const id = item.id || item.item_id;
        const draftLine = draftPackages.find((p) => String(p.package_id) === String(id));
        return (draftLine?.quantity ?? 0) <= 0;
      }),
    [venueAddonCatalog, draftPackages]
  );
  const addonsSubtotal = sumDraftPackageLines(draftPackages);
  const bookingAmountNumber = parseMoney(detailsDraft.bookingAmount) ?? 0;
  const addOnsAmountNumber = parseMoney(detailsDraft.addOnsAmount) ?? 0;
  const discountAmountNumber = parseMoney(detailsDraft.discountAmount) ?? 0;
  const venueSubtotalNumber = roundMoney(bookingAmountNumber + addOnsAmountNumber);
  const venueTotalNumber = roundMoney(Math.max(0, venueSubtotalNumber - discountAmountNumber));
  const isTbcBooking = Boolean(booking) && needsVenuePricingConfirmation(booking);
  const hasListedSpaceRate = Boolean(booking) && bookingHasListedSpaceRate(booking, req);
  const canEditTbcPricing =
    isTbcBooking &&
    ['pending', 'on_hold'].includes(
      String(booking?.status ?? booking?.booking_status ?? 'pending').toLowerCase()
    ) &&
    !isPastBookingDate(booking);
  const canEditPricingFields = canEditTbcPricing || isPriceEditMode;
  const draftFreeBookingMode = detailsDraft.freeBookingMode;
  const chargeableVenueSubtotal = useMemo(
    () =>
      resolveChargeableVenueSubtotal({
        baseAmount: bookingAmountNumber,
        addOnsTotal: addOnsAmountNumber,
        discountAmount: discountAmountNumber,
        mode: draftFreeBookingMode
      }),
    [bookingAmountNumber, addOnsAmountNumber, discountAmountNumber, draftFreeBookingMode]
  );
  const drawerFeeBreakdown = useMemo(() => {
    if (chargeableVenueSubtotal <= 0) {
      return { venueTotal: 0, platformFee: 0, processingFee: 0, chargeTotal: 0 };
    }
    const computed = computePaymentFeeBreakdown(chargeableVenueSubtotal, {
      isPlatformBooking: !isManualBookingSource(booking),
      venueId: bookingVenueId
    });
    return {
      venueTotal: chargeableVenueSubtotal,
      platformFee: computed.platformFee,
      processingFee: computed.processingFee,
      chargeTotal: computed.chargeTotal
    };
  }, [chargeableVenueSubtotal, booking, bookingVenueId]);
  const chargeTotalNumber = drawerFeeBreakdown.chargeTotal;
  const platformFeeDisplay = drawerFeeBreakdown.platformFee;
  const processingFeeDisplay = drawerFeeBreakdown.processingFee;
  const showFeeBreakdown =
    chargeableVenueSubtotal > 0 ||
    venueTotalNumber > 0 ||
    canEditTbcPricing ||
    bookingAmountNumber > 0 ||
    addOnsAmountNumber > 0;
  const amountPaidNumber = parseMoney(detailsDraft.amountPaid) ?? 0;
  const bookingIsSpaceFree = detailsDraft.freeBookingMode === FREE_BOOKING_MODE.SPACE_ONLY;
  const bookingIsPaid = ['completed', 'paid', '1'].includes(
    String(booking?.payment_status ?? booking?.paymentStatus ?? 'pending').toLowerCase()
  );
  const calculatedPendingAmount = bookingIsPaid
    ? 0
    : Math.max(0, roundMoney(chargeTotalNumber - amountPaidNumber));
  const paymentDisplayFields = useMemo(
    () => resolveBookingPaymentDisplayFields(booking),
    [booking]
  );

  const resolvedBookingAmounts = useMemo(
    () => (booking ? resolveBookingAmounts(booking, { venueId: bookingVenueId }) : null),
    [booking, bookingVenueId]
  );
  const bookingIsFree = bookingIsSpaceFree && chargeableVenueSubtotal <= 0;
  const effectiveBookingForPayment = useMemo(() => {
    if (!booking) return null;
    const pending = parseMoney(detailsDraft.amountPending) ?? calculatedPendingAmount;
    const freeModeRequirements = buildFreeBookingModeRequirements(detailsDraft.freeBookingMode);
    return {
      ...booking,
      free_booking: false,
      total_amount: chargeTotalNumber,
      final_amount: chargeTotalNumber,
      amount_pending: pending,
      amount_paid: amountPaidNumber,
      add_ons_total: addOnsAmountNumber,
      packages: draftPackages,
      requirements: {
        ...req,
        ...freeModeRequirements,
        booking_amount: venueTotalNumber,
        amount_pending: pending,
        amount_paid: amountPaidNumber,
        add_ons_total: addOnsAmountNumber,
        packages: draftPackages,
        platform_fee: platformFeeDisplay,
        processing_fee: processingFeeDisplay
      }
    };
  }, [
    booking,
    req,
    detailsDraft.freeBookingMode,
    detailsDraft.amountPending,
    chargeTotalNumber,
    venueTotalNumber,
    calculatedPendingAmount,
    amountPaidNumber,
    addOnsAmountNumber,
    platformFeeDisplay,
    processingFeeDisplay,
    draftPackages
  ]);

  useEffect(() => {
    if (!booking || isPastBooking) return;
    const nextAddOns = String(sumDraftPackageLines(draftPackages));
    setDetailsDraft((prev) => {
      if (prev.addOnsAmount === nextAddOns) return prev;
      return { ...prev, addOnsAmount: nextAddOns };
    });
  }, [draftPackages, booking, isPastBooking]);

  const pendingDraftChanges = useMemo(() => {
    if (isBookingReadOnly) return [];
    return buildUnsavedDraftChangeSummary(booking, detailsDraft, draftPackages, {
      venueId: bookingVenueId,
      pendingManuallyEdited,
      bookingAmountManuallyEdited,
      amountPaidManuallyEdited
    });
  }, [
    booking,
    detailsDraft,
    draftPackages,
    bookingVenueId,
    isBookingReadOnly,
    pendingManuallyEdited,
    bookingAmountManuallyEdited,
    amountPaidManuallyEdited
  ]);
  const hasPendingDraftChanges = pendingDraftChanges.length > 0;
  const hasLeftPanelDraftChanges = useMemo(
    () => pendingDraftChanges.some((change) => LEFT_PANEL_DRAFT_CHANGE_LABELS.has(change.label)),
    [pendingDraftChanges]
  );
  const hasBookingDetailsDraftChanges = useMemo(
    () =>
      pendingDraftChanges.some(
        (change) =>
          LEFT_PANEL_DRAFT_CHANGE_LABELS.has(change.label) && change.label !== 'Add-ons'
      ),
    [pendingDraftChanges]
  );
  const hasAddOnsDraftChanges = useMemo(
    () => pendingDraftChanges.some((change) => change.label === 'Add-ons'),
    [pendingDraftChanges]
  );
  const hasRightPanelPriceDraftChanges = useMemo(
    () => pendingDraftChanges.some((change) => RIGHT_PANEL_PRICE_CHANGE_LABELS.has(change.label)),
    [pendingDraftChanges]
  );
  const hasCloseableDraftChanges = hasLeftPanelDraftChanges || hasRightPanelPriceDraftChanges;

  const displayPayableAmounts = useMemo(() => {
    if (!booking) return null;
    return resolveBookingPayableBreakdown(booking, {
      venueId: bookingVenueId,
      draftPricing: {
        useDraft: true,
        baseAmount: bookingAmountNumber,
        addOnsTotal: addOnsAmountNumber,
        venueTotal: venueTotalNumber,
        discountAmount: discountAmountNumber,
        freeBookingMode: detailsDraft.freeBookingMode,
        amountPaid: amountPaidNumber
      }
    });
  }, [
    booking,
    bookingVenueId,
    bookingAmountNumber,
    addOnsAmountNumber,
    venueTotalNumber,
    amountPaidNumber,
    discountAmountNumber,
    detailsDraft.freeBookingMode
  ]);

  const displayGrandTotal = displayPayableAmounts?.grandTotal ?? chargeTotalNumber;
  const displayBaseAmount = isPastBooking
    ? (displayPayableAmounts?.baseAmount ?? bookingAmountNumber)
    : (bookingAmountNumber ?? displayPayableAmounts?.baseAmount);
  const displayAddOnsTotal = displayPayableAmounts?.addOnsTotal ?? addonsSubtotal;
  const displayPlatformFee = displayPayableAmounts?.platformFee ?? platformFeeDisplay;
  const displayProcessingFee = displayPayableAmounts?.processingFee ?? processingFeeDisplay;
  const displayAmountPending = bookingIsPaid
    ? 0
    : (displayPayableAmounts?.amountPending ??
      Math.max(0, roundMoney(displayGrandTotal - amountPaidNumber)));

  useEffect(() => {
    if (!booking || pricingRecalcVersion === 0 || !pricingRecalcTrigger || isPastBooking) return;
    if (bookingAmountManuallyEdited) return;
    if (!detailsDraft.eventDate || !detailsDraft.eventStartTime || !detailsDraft.eventEndTime) {
      return;
    }

    let cancelled = false;
    (async () => {
      const nextBase = await resolveEditedBookingBaseAmount({
        venueDashboardApi: venueDashboardApiService,
        booking,
        venueId: bookingVenueId,
        bookingType: detailsDraft.bookingType,
        eventDate: detailsDraft.eventDate,
        startTime: detailsDraft.eventStartTime,
        endTime: detailsDraft.eventEndTime,
        attendees: detailsDraft.attendees,
        currentBaseAmount: parseMoney(detailsDraft.bookingAmount),
        timezone: venueTimezone,
        trigger: pricingRecalcTrigger,
        bookingAmountManuallyEdited
      });
      if (cancelled || nextBase == null) return;
      const nextValue = String(nextBase);
      setDetailsDraft((prev) => {
        if (prev.bookingAmount === nextValue) return prev;
        return { ...prev, bookingAmount: nextValue };
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    booking,
    bookingVenueId,
    venueTimezone,
    detailsDraft.bookingType,
    detailsDraft.eventDate,
    detailsDraft.eventStartTime,
    detailsDraft.eventEndTime,
    detailsDraft.attendees,
    detailsDraft.bookingAmount,
    detailsDraft.freeBookingMode,
    bookingAmountManuallyEdited,
    pricingRecalcVersion,
    pricingRecalcTrigger,
    isPastBooking
  ]);

  useEffect(() => {
    if (!booking || !needsVenuePricingConfirmation(booking) || isPastBooking) return;
    if (!bookingHasListedSpaceRate(booking)) return;
    if (bookingAmountManuallyEdited) return;
    const base = parseMoney(detailsDraft.bookingAmount);
    if (base != null && base > 0) return;
    if (!detailsDraft.eventDate || !detailsDraft.eventStartTime || !detailsDraft.eventEndTime) {
      return;
    }
    setPricingRecalcTrigger('mode');
    setPricingRecalcVersion((version) => version + 1);
  }, [
    booking?.id,
    detailsDraft.eventDate,
    detailsDraft.eventStartTime,
    detailsDraft.eventEndTime,
    detailsDraft.bookingAmount,
    detailsDraft.freeBookingMode,
    booking,
    bookingAmountManuallyEdited,
    isPastBooking
  ]);

  const bookingStatusForPayment = booking?.status || booking?.booking_status || 'pending';
  const paymentStatusNormalized = String(
    booking?.payment_status ?? booking?.paymentStatus ?? 'pending'
  ).toLowerCase();
  const paymentIsSettled = ['completed', 'paid', '1', 'refunded'].includes(paymentStatusNormalized);
  const paymentApprovedForCollection = isApprovedBookingStatus(bookingStatusForPayment);
  const paymentPendingForCollection = !paymentIsSettled;
  const outstandingBalance = Math.max(
    resolvedBookingAmounts?.amountPending ?? 0,
    resolvedBookingAmounts?.grandTotal ?? 0,
    calculatedPendingAmount,
    chargeTotalNumber
  );
  const hasOutstandingBalance = outstandingBalance > 0;
  const organizerPaymentDue =
    paymentApprovedForCollection &&
    paymentPendingForCollection &&
    !bookingIsFree &&
    (bookingNeedsOrganizerPayment(booking) || hasOutstandingBalance);
  const showPaymentCollectionPanel =
    Boolean(booking) && organizerPaymentDue && !isTbcBooking && !isPaymentEditMode;

  const paymentContextFetchKey = showPaymentCollectionPanel && bookingIdResolved
    ? [
        bookingIdResolved,
        bookingVenueId,
        venuePaymentLink,
        bookingStatusForPayment,
        paymentStatusNormalized
      ].join('|')
    : null;

  useEffect(() => {
    setPaymentCollectionMethod('online');
    setReminderSuccess('');
    setReminderError('');
    setPaymentContext(null);
  }, [bookingIdResolved]);

  useEffect(() => {
    if (!paymentContextFetchKey || !bookingIdResolved) {
      return;
    }

    let cancelled = false;
    setPaymentContextLoading(true);
    resolveBookingPaymentContext(
      bookingIdResolved,
      bookingVenueId,
      venuePaymentLink,
      { booking }
    )
      .then((ctx) => {
        if (!cancelled) setPaymentContext(ctx);
      })
      .catch(() => {
        if (!cancelled) setPaymentContext(null);
      })
      .finally(() => {
        if (!cancelled) setPaymentContextLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [paymentContextFetchKey, booking, bookingIdResolved, bookingVenueId, venuePaymentLink]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (cancelConfirmOpen) {
        if (!isConfirming) setCancelConfirmOpen(false);
        return;
      }
      if (closePromptOpen) {
        if (!closePromptBusy) setClosePromptOpen(false);
        return;
      }
      if (isPaymentEditMode && !hasCloseableDraftChanges) {
        applyBookingToDraft(booking);
        setIsPaymentEditMode(false);
        setMarkAsPaidFlow(false);
        setDetailsSaveError('');
        return;
      }
      if (hasCloseableDraftChanges) {
        setClosePromptOpen(true);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- escape handler reads latest draft helpers from closure
  }, [
    cancelConfirmOpen,
    closePromptOpen,
    closePromptBusy,
    hasCloseableDraftChanges,
    isPaymentEditMode,
    booking,
    onClose,
    isConfirming
  ]);

  const sidebarPricingMeta = useMemo(() => {
    if (!booking) {
      return {
        pricingMode: 'exclusive',
        bookingType: 'table',
        headcount: null,
        perPersonRate: null,
        spaceHourlyRate: null
      };
    }
    const meta = readPricingMetadata(booking, req);
    const draftType = detailsDraft.bookingType || meta.bookingType;
    const draftMode = normalizeBookingModeApi(draftType);
    const attendeeCount = Number.parseInt(detailsDraft.attendees, 10);
    const guests =
      draftMode === 'per_person'
        ? Number.isFinite(attendeeCount) && attendeeCount > 0
          ? attendeeCount
          : meta.headcount || 1
        : null;
    const confirmedPerPersonRate =
      draftMode === 'per_person' ? resolveConfirmedPerPersonRate({ booking, req }) : meta.perPersonRate;
    const confirmedSpaceHourlyRate =
      draftMode === 'exclusive' ? resolveConfirmedSpaceHourlyRate({ booking, req }) : meta.spaceHourlyRate;
    return {
      ...meta,
      pricingMode: draftMode,
      bookingType: draftType,
      headcount: guests,
      perPersonRate: confirmedPerPersonRate,
      spaceHourlyRate: confirmedSpaceHourlyRate
    };
  }, [booking, req, detailsDraft.bookingType, detailsDraft.attendees]);

  /** Newest-first timeline for Change log (must stay before `if (!booking) return null`). */
  const sortedChangelogEntries = useMemo(
    () =>
      buildSortedChangelogEntries(
        auditLogEntries,
        booking,
        booking?.created_at
          ? formatDate(new Date(booking.created_at), { day: 'numeric', month: 'long', year: 'numeric' })
          : '—'
      ),
    [auditLogEntries, booking]
  );

  if (!booking) return null;

  const bookingStatus = booking.status || 'pending';
  const paymentRaw = booking.payment_status ?? booking.paymentStatus ?? 'pending';
  const paymentStr = String(paymentRaw).toLowerCase();
  const paymentClass = ['completed', 'paid', '1'].includes(paymentStr) ? 'completed' : ['refunded'].includes(paymentStr) ? 'refunded' : 'pending';
  const showPaymentReminder =
    isApprovedBookingStatus(bookingStatus) &&
    !paymentIsSettled &&
    !bookingIsFree &&
    hasOutstandingBalance;

  const handleSendPaymentReminder = async () => {
    const bid = booking.id ?? booking.booking_id ?? bookingIdResolved;
    if (!bid || bookingIsFree) return;
    setSendingReminder(true);
    setReminderError('');
    setReminderSuccess('');
    try {
      const linkBookingId = booking.id ?? booking.booking_id ?? bid;
      const paymentContext = await resolveBookingPaymentContext(
        linkBookingId,
        venueId || booking?.venue_id,
        venuePaymentLink,
        { booking: effectiveBookingForPayment }
      );
      const result = await venueDashboardApiService.sendBookingApprovedEmail(
        bid,
        { ...buildApprovalEmailParams(paymentContext), is_payment_reminder: true }
      );
      const nextCount =
        result?.payment_reminder_sent_count ?? getPaymentReminderSentCount(booking) + 1;
      const existingReq = parseBookingRequirements(booking);
      onBookingLocalUpdate?.(String(bid), {
        requirements: {
          ...existingReq,
          payment_reminder_sent_count: nextCount,
          payment_reminder_last_sent_at: new Date().toISOString()
        }
      });
      setReminderSuccess(paymentEmailFeedbackMessage(paymentContext.mode));
    } catch (err) {
      setReminderError(err?.message || 'Could not send payment reminder. Please try again.');
    } finally {
      setSendingReminder(false);
    }
  };

  const paymentReminderSentCount = getPaymentReminderSentCount(booking);

  const renderPaymentReminderFeedback = () => (
    <>
      {reminderSuccess ? (
        <p className="manage-drawer-reminder-success">{reminderSuccess}</p>
      ) : null}
      {reminderError ? (
        <p className="manage-drawer-reminder-error" role="alert">
          {reminderError}
        </p>
      ) : null}
    </>
  );

  const renderPaymentReminderSection = () => {
    if (!showPaymentReminder) return null;
    return (
      <div className="manage-drawer-price-reminder">
        <p className="manage-drawer-price-reminder-copy">
          Payment reminder:{' '}
          <strong>{paymentReminderSentCount} sent</strong>
        </p>
        <button
          type="button"
          className="manage-drawer-price-reminder-btn"
          disabled={sendingReminder || (showPaymentCollectionPanel && paymentContextLoading)}
          onClick={handleSendPaymentReminder}
        >
          {sendingReminder
            ? 'Sending…'
            : showPaymentCollectionPanel && paymentContextLoading
              ? 'Loading…'
              : 'Send reminder'}
        </button>
        {renderPaymentReminderFeedback()}
      </div>
    );
  };

  const paymentOkForFeedback =
    bookingIsFree || ['completed', 'paid', '1'].includes(paymentStr);
  const feedbackSubmitted = Boolean(feedbackMeta?.feedback?.rating);
  const feedbackRequestSentCount =
    feedbackMeta?.feedback_request_sent_count ?? getFeedbackRequestSentCount(booking);
  const showFeedbackRequest =
    isPastBooking &&
    !CANCELLED_BOOKING_STATUSES.has(String(bookingStatus || '').toLowerCase()) &&
    paymentOkForFeedback &&
    !feedbackSubmitted;

  const handleSendFeedbackRequest = async () => {
    const bid = booking.id ?? booking.booking_id ?? bookingIdResolved;
    if (!bid || feedbackSubmitted) return;
    setSendingFeedbackRequest(true);
    setFeedbackRequestError('');
    setFeedbackRequestSuccess('');
    try {
      const result = await venueDashboardApiService.sendFeedbackRequest(bid);
      const nextCount =
        result?.feedback_request_sent_count ?? feedbackRequestSentCount + 1;
      const existingReq = parseBookingRequirements(booking);
      onBookingLocalUpdate?.(String(bid), {
        requirements: {
          ...existingReq,
          feedback_request_sent_count: nextCount,
          feedback_request_sent_at: new Date().toISOString(),
        },
      });
      setFeedbackMeta((prev) => ({
        ...(prev || {}),
        feedback_request_sent_count: nextCount,
        feedback: prev?.feedback ?? null,
      }));
      const warning = result?.warning;
      setFeedbackRequestSuccess(
        warning
          ? `${warning}. The organizer should still receive the email.`
          : 'Feedback request email sent to the organizer.'
      );
    } catch (err) {
      setFeedbackRequestError(err?.message || 'Could not send feedback request. Please try again.');
    } finally {
      setSendingFeedbackRequest(false);
    }
  };

  const renderFeedbackSection = () => {
    if (!showFeedbackRequest && !feedbackSubmitted) return null;
    if (feedbackLoading && !feedbackMeta) return null;

    if (feedbackSubmitted) {
      const fb = feedbackMeta?.feedback;
      return (
        <div className="manage-drawer-price-reminder manage-drawer-feedback-section">
          <p className="manage-drawer-price-reminder-copy">
            Customer feedback:{' '}
            <strong>{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</strong>
          </p>
          {fb.comment ? (
            <p className="manage-drawer-feedback-comment">{fb.comment}</p>
          ) : null}
        </div>
      );
    }

    return (
      <div className="manage-drawer-price-reminder manage-drawer-feedback-section">
        <p className="manage-drawer-price-reminder-copy">
          Feedback request:{' '}
          <strong>{feedbackRequestSentCount} sent</strong>
        </p>
        <button
          type="button"
          className="manage-drawer-price-reminder-btn"
          disabled={sendingFeedbackRequest}
          onClick={handleSendFeedbackRequest}
        >
          {sendingFeedbackRequest ? 'Sending…' : feedbackRequestSentCount > 0 ? 'Resend request' : 'Send feedback request'}
        </button>
        {feedbackRequestSuccess ? (
          <p className="manage-drawer-reminder-success">{feedbackRequestSuccess}</p>
        ) : null}
        {feedbackRequestError ? (
          <p className="manage-drawer-reminder-error" role="alert">
            {feedbackRequestError}
          </p>
        ) : null}
      </div>
    );
  };

  const currentPaymentStatusAction =
    paymentClass === 'completed' ? 'completed' : paymentClass === 'refunded' ? 'refunded' : 'pending';

  const handleUpdatePaymentStatus = async () => {
    if (bookingIsPaid) return;
    if (!paymentStatusAction || paymentStatusAction === currentPaymentStatusAction || isConfirming) {
      return;
    }
    const bookingId = booking.id ?? booking.booking_id ?? bookingIdResolved;
    if (!bookingId || !onUpdateBookingStatus) return;

    setIsConfirming(true);
    setDetailsSaveError('');
    try {
      const statusResult = await onUpdateBookingStatus(
        bookingId,
        null,
        '',
        paymentStatusAction,
        effectiveBookingForPayment ?? booking
      );
      if (statusResult?.ok === false) {
        window.alert(statusResult.errorMessage || 'Unable to update payment status. Please try again.');
        onBookingRefresh?.({ silent: true });
        return;
      }

      const apiPaymentStatus =
        paymentStatusAction === 'completed' ? 'paid' : paymentStatusAction;
      onBookingLocalUpdate?.(String(bookingId), {
        payment_status: apiPaymentStatus,
        paymentStatus: apiPaymentStatus,
        ...(paymentStatusAction === 'pending'
          ? {
              amount_paid: 0,
              amountPaid: 0,
              amount_pending: chargeTotalNumber,
              amountPending: chargeTotalNumber
            }
          : paymentStatusAction === 'completed'
            ? {
                amount_paid: chargeTotalNumber,
                amountPaid: chargeTotalNumber,
                amount_pending: 0,
                amountPending: 0
              }
            : {})
      });

      setPaymentStatusAction('');
      setIsPaymentEditMode(false);
      onBookingRefresh?.({ silent: true });
    } catch (error) {
      setDetailsSaveError(error?.message || 'Failed to update payment status.');
    } finally {
      setIsConfirming(false);
    }
  };

  const renderPaymentStatusUpdateSection = () => {
    if (bookingIsPaid) return null;

    return (
    <div className="manage-drawer-confirmation manage-drawer-confirmation--summary">
      <p className="manage-drawer-field-label">Update payment status</p>
      <div className="manage-drawer-radio-row">
        <label className="manage-drawer-radio-option">
          <input
            type="radio"
            name="paymentStatusAction"
            value="pending"
            checked={paymentStatusAction === 'pending'}
            onChange={(e) => setPaymentStatusAction(e.target.value)}
            className="manage-drawer-radio-input"
          />
          <span className="manage-drawer-radio-custom" />
          <span className="manage-drawer-radio-text">Pending</span>
        </label>
        <label className="manage-drawer-radio-option">
          <input
            type="radio"
            name="paymentStatusAction"
            value="completed"
            checked={paymentStatusAction === 'completed'}
            onChange={(e) => setPaymentStatusAction(e.target.value)}
            className="manage-drawer-radio-input"
          />
          <span className="manage-drawer-radio-custom" />
          <span className="manage-drawer-radio-text">Paid</span>
        </label>
        <label className="manage-drawer-radio-option">
          <input
            type="radio"
            name="paymentStatusAction"
            value="refunded"
            checked={paymentStatusAction === 'refunded'}
            onChange={(e) => setPaymentStatusAction(e.target.value)}
            className="manage-drawer-radio-input"
          />
          <span className="manage-drawer-radio-custom" />
          <span className="manage-drawer-radio-text">Refunded</span>
        </label>
      </div>
      {paymentStatusAction && paymentStatusAction !== currentPaymentStatusAction ? (
        <button
          type="button"
          className="manage-drawer-save-addons-btn"
          disabled={isConfirming}
          onClick={handleUpdatePaymentStatus}
        >
          {isConfirming ? 'Updating…' : 'Update payment status'}
        </button>
      ) : null}
    </div>
    );
  };

  const contactNumber = organizerContact.phone;
  const activeInvoiceName = getInvoiceDisplayName(activeInvoice);
  const durationHrs = calculateDurationHours(detailsDraft.eventStartTime, detailsDraft.eventEndTime);
  const pricingBreakdown = formatPricingBreakdownLabel(sidebarPricingMeta, durationHrs, {
    storedBaseAmount: displayBaseAmount
  });
  const currentFeeLines = formatFeeBreakdownLines({
    space_fee: displayBaseAmount,
    add_ons: displayAddOnsTotal,
    discount: discountAmountNumber,
    platform_fee: displayPlatformFee,
    processing_fee: displayProcessingFee,
    total_payable: displayGrandTotal,
    space_detail: pricingBreakdown?.detail || null
  });
  const bookingDateLabel = detailsDraft.eventDate
    ? formatDate(parseDateOnly(detailsDraft.eventDate), { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const timeRangeLabel =
    detailsDraft.eventStartTime && detailsDraft.eventEndTime
      ? `${formatTime(detailsDraft.eventStartTime)} - ${formatTime(detailsDraft.eventEndTime)} (${durationHrs}hrs)`
      : '—';
  const perPersonBookingOffered = isPerPersonBookingEnabled(bookingRules);
  const isExistingPerPersonBooking = resolveBookingTypeFromRecord(booking) === 'table';
  const showPerPersonBookingOption = perPersonBookingOffered || isExistingPerPersonBooking;
  const bookingTypeLabel = detailsDraft.bookingType === 'full-venue' ? 'Full venue hire' : 'Per person';

  const sendInvoiceUploadedPaymentEmail = async (bookingId) => {
    try {
      const paymentContext = await resolveBookingPaymentContext(
        bookingId,
        venueId || booking?.venue_id,
        venuePaymentLink,
        { booking: effectiveBookingForPayment }
      );
      if (!shouldSendPaymentEmailAfterInvoiceUpload(paymentContext.mode)) {
        return;
      }
      await venueDashboardApiService.sendInvoiceUploadedEmail(
        bookingId,
        buildApprovalEmailParams(paymentContext)
      );
    } catch {
      // Non-blocking: venue can use "Send payment reminder" if email fails
    }
  };

  const handleInvoiceDownload = async (file) => {
    if (!bookingIdResolved || !file?.id) return;
    try {
      const response = await venueDashboardApiService.getBookingOwnerInvoiceDownloadUrl(bookingIdResolved, file.id, {
        ownerId: ownerInvoiceActor
      });
      if (response?.download_url) {
        window.open(response.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setInvoiceError(error?.message || 'Failed to download invoice');
    }
  };

  const handleInvoiceUpload = async (file) => {
    if (!file || !bookingIdResolved || !ownerInvoiceActor) return;
    try {
      setInvoiceUploading(true);
      setInvoiceError('');
      await venueDashboardApiService.uploadBookingOwnerInvoice(bookingIdResolved, file, {
        ownerId: ownerInvoiceActor,
        replaceFileId: activeInvoice?.id
      });
      await loadInvoiceFiles();
      await sendInvoiceUploadedPaymentEmail(bookingIdResolved);
    } catch (error) {
      setInvoiceError(error?.message || 'Failed to upload invoice');
    } finally {
      setInvoiceUploading(false);
    }
  };

  const handleInvoiceDelete = async () => {
    if (!bookingIdResolved || !activeInvoice?.id || !ownerInvoiceActor) return;
    try {
      setInvoiceError('');
      await venueDashboardApiService.deleteBookingOwnerInvoice(bookingIdResolved, activeInvoice.id, {
        ownerId: ownerInvoiceActor
      });
      await loadInvoiceFiles();
    } catch (error) {
      setInvoiceError(error?.message || 'Failed to remove invoice');
    }
  };

  const buildMinimalPackagePayload = () =>
    draftPackages
      .filter((p) => p.quantity > 0)
      .map((p) => {
        const line = {
          name: p.name,
          quantity: p.quantity,
          unit_price: p.unit_price,
          amount: Number(p.total) || 0
        };
        if (p.package_id != null) {
          line.package_id = p.package_id;
        }
        return line;
      });

  const updateDetailsDraft = (field, value) => {
    setDetailsDraft((prev) => {
      const next = {
        ...prev,
        [field]: value
      };
      if (field === 'catering' && !isInHouseCateringPreference(value)) {
        next.hasDietaryRestrictions = false;
        next.dietaryRestrictions = '';
      }
      if (field === 'hasDietaryRestrictions' && !value) {
        next.dietaryRestrictions = '';
      }
      return next;
    });
    setDetailsDirty(true);
    setDetailsSaveError('');
    if (field === 'amountPending') {
      setPendingManuallyEdited(true);
    }
    if (field === 'amountPaid') {
      setAmountPaidManuallyEdited(true);
    }
    if (field === 'bookingAmount') {
      setBookingAmountManuallyEdited(true);
      setPricingRecalcTrigger(null);
    }
    if (field === 'discountCode' || field === 'discountAmount') {
      setDiscountError('');
    }
    if (field === 'attendees' && hasListedSpaceRate) {
      setBookingAmountManuallyEdited(false);
      setPricingRecalcTrigger('headcount');
      setPricingRecalcVersion((version) => version + 1);
    }
    if (field === 'bookingType' && hasListedSpaceRate) {
      setBookingAmountManuallyEdited(false);
      setPricingRecalcTrigger('mode');
      setPricingRecalcVersion((version) => version + 1);
    }
  };

  const handleApplyDiscountCode = async () => {
    const code = detailsDraft.discountCode?.trim();
    if (!code) {
      setDiscountError('Enter a discount code.');
      return;
    }
    const subtotal = venueSubtotalNumber;
    if (subtotal <= 0) {
      setDiscountError('Set a booking amount before applying a discount code.');
      return;
    }
    setDiscountValidating(true);
    setDiscountError('');
    try {
      const result = await discountCodeApiService.validateDiscountCode(code, subtotal);
      if (result?.is_valid) {
        updateDetailsDraft('discountAmount', String(result.discount_amount ?? 0));
      } else {
        setDiscountError(result?.message || 'Invalid discount code.');
      }
    } catch (error) {
      setDiscountError(error?.message || 'Could not validate discount code.');
    } finally {
      setDiscountValidating(false);
    }
  };

  const handleFreeBookingModeChange = (nextMode) => {
    if (bookingIsPaid) return;

    const normalized = normalizeFreeBookingMode(nextMode || FREE_BOOKING_MODE.NONE);
    if (
      normalized !== FREE_BOOKING_MODE.NONE &&
      detailsDraft.freeBookingMode === FREE_BOOKING_MODE.NONE
    ) {
      freeTogglePricingSnapshotRef.current = lastPricedSnapshotFromDraftPricing({
        bookingAmount: detailsDraft.bookingAmount,
        addOnsAmount: detailsDraft.addOnsAmount,
        amountPaid: detailsDraft.amountPaid,
        amountPending: detailsDraft.amountPending,
        draftPackages
      });
    }
    setDetailsDraft((prev) => ({
      ...prev,
      freeBookingMode: normalized
    }));
    setDetailsDirty(true);
    setIsPriceEditMode(true);
    setDetailsSaveError('');
  };

  const renderFreeSpaceWaiver = () => {
    if (isBookingReadOnly || isPastBooking) return null;
    return (
      <div className="manage-drawer-free-space-bar">
        <label className="manage-drawer-free-space-check">
          <input
            type="checkbox"
            checked={bookingIsSpaceFree}
            disabled={bookingIsPaid}
            onChange={(event) =>
              handleFreeBookingModeChange(
                event.target.checked ? FREE_BOOKING_MODE.SPACE_ONLY : FREE_BOOKING_MODE.NONE
              )
            }
          />
          <span>
            <strong>Free space</strong>
            <em>Add-ons still charged if any</em>
          </span>
        </label>
      </div>
    );
  };

  const getBookingNoteBadge = (note) => {
    const source = note?.source || '';
    if (source === 'venue_internal') {
      return { label: 'Venue', variant: 'owner' };
    }
    if (source === 'manual_dashboard') {
      return { label: 'Manual booking', variant: 'organiser' };
    }
    return { label: 'Event organiser', variant: 'organiser' };
  };

  const bumpCatalogAddonQty = (catalogItem, delta) => {
    if (isBookingReadOnly) return;
    const packageId = catalogItem.id || catalogItem.item_id;
    setDraftPackages((prev) => {
      const existing = prev.find((p) => String(p.package_id) === String(packageId));
      const currentQty = existing?.quantity ?? 0;
      const newQty = Math.max(0, currentQty + delta);
      const rest = prev.filter((p) => String(p.package_id) !== String(packageId));
      if (newQty <= 0) return rest;
      const existingTotal = existing?.total != null ? Number(existing.total) : null;
      const existingDerivedUnit =
        existingTotal != null && Number.isFinite(existingTotal) && currentQty > 0
          ? roundMoney(existingTotal / currentQty)
          : null;
      const existingUnit =
        existing && existing.unit_price != null ? Number(existing.unit_price) : null;
      const catalogUnit =
        Number(catalogItem?.price ?? catalogItem?.price_per_unit ?? catalogItem?.unit_price ?? 0) || 0;
      const unit =
        existingDerivedUnit != null
          ? existingDerivedUnit
          : existingUnit != null && Number.isFinite(existingUnit)
          ? roundMoney(existingUnit)
          : roundMoney(catalogUnit);
      const name = catalogItem?.name || catalogItem?.item_name || 'Add-on';
      return [
        ...rest,
        {
          _key: existing?._key ?? `cat-${packageId}`,
          package_id: packageId,
          name,
          quantity: newQty,
          unit_price: roundMoney(unit),
          total: roundMoney(unit * newQty)
        }
      ];
    });
    setAddonsDirty(true);
  };

  const findOrphanDraftLine = (rowRef, lines) => {
    if (rowRef && typeof rowRef === 'object' && rowRef._key) {
      return lines.find((p) => p._key === rowRef._key);
    }
    return lines.find((p) => String(p.package_id) === String(rowRef));
  };

  const bumpOrphanAddonQty = (rowRef, delta) => {
    if (isBookingReadOnly) return;
    setDraftPackages((prev) => {
      const existing = findOrphanDraftLine(rowRef, prev);
      if (!existing) return prev;
      const newQty = Math.max(0, existing.quantity + delta);
      const rest = prev.filter((p) => p._key !== existing._key);
      if (newQty <= 0) return rest;
      const existingTotal = existing.total != null ? Number(existing.total) : null;
      const existingDerivedUnit =
        existingTotal != null && Number.isFinite(existingTotal) && existing.quantity > 0
          ? roundMoney(existingTotal / existing.quantity)
          : null;
      const unit =
        existingDerivedUnit != null
          ? existingDerivedUnit
          : roundMoney(Number(existing.unit_price) || 0);
      return [
        ...rest,
        {
          ...existing,
          quantity: newQty,
          unit_price: roundMoney(unit),
          total: roundMoney(unit * newQty)
        }
      ];
    });
    setAddonsDirty(true);
  };

  const removeOrphanAddon = (rowRef) => {
    if (isBookingReadOnly) return;
    setDraftPackages((prev) => {
      const existing = findOrphanDraftLine(rowRef, prev);
      if (!existing) return prev;
      return prev.filter((p) => p._key !== existing._key);
    });
    setAddonsDirty(true);
  };

  const saveDraftBookingChanges = async ({ markAsPaid = false } = {}) => {
    if (isBookingReadOnly) {
      throw new Error('Past bookings cannot be edited.');
    }
    const idForPut = booking.id ?? booking.booking_id ?? bookingIdResolved;
    if (!idForPut) {
      throw new Error('Missing booking id.');
    }

    const reqBase = parseBookingRequirements(booking);
    const attendeesParsed = Number.parseInt(detailsDraft.attendees, 10);
    const existingAttendees = bookingHeadcount(booking, reqBase);
    const attendeesCandidate = Number.isFinite(attendeesParsed)
      ? attendeesParsed
      : (existingAttendees ?? 1);
    const attendees = Math.max(1, attendeesCandidate);
    const packageLines = buildMinimalPackagePayload();
    const addOnsTotal = parseMoney(detailsDraft.addOnsAmount) ?? sumDraftPackageLines(packageLines);
    const durationHours = calculateDurationHours(
      detailsDraft.eventStartTime,
      detailsDraft.eventEndTime
    );
    const baseAmountForSave = parseMoney(detailsDraft.bookingAmount) ?? 0;
    const addOnsTotalValue = addOnsTotal;
    const discountForSave = parseMoney(detailsDraft.discountAmount) ?? 0;
    const paidAmount = markAsPaid
      ? (parseMoney(detailsDraft.amountPaid) ?? amountPaidNumber)
      : amountPaidNumber;
    const packagePayload = packageLines;
    const venueTotalForSave = roundMoney(
      Math.max(0, baseAmountForSave + addOnsTotalValue - discountForSave)
    );
    const freeMode = detailsDraft.freeBookingMode ?? FREE_BOOKING_MODE.NONE;
    const chargeableVenueForSave = resolveChargeableVenueSubtotal({
      baseAmount: baseAmountForSave,
      addOnsTotal: addOnsTotalValue,
      discountAmount: discountForSave,
      mode: freeMode
    });
    const feeBreakdownForSave =
      chargeableVenueForSave <= 0
        ? null
        : computePaymentFeeBreakdown(chargeableVenueForSave, {
            isPlatformBooking: !isManualBookingSource(booking),
            venueId: bookingVenueId
          });
    const totalAmount = feeBreakdownForSave ? feeBreakdownForSave.chargeTotal : 0;
    const feeSnapshot = buildFeeBreakdownSnapshot({
      spaceFee: baseAmountForSave,
      addOnsTotal: addOnsTotalValue,
      discountAmount: discountForSave,
      chargeableVenue: chargeableVenueForSave,
      isPlatformBooking: !isManualBookingSource(booking),
      venueId: bookingVenueId,
      spaceDetail: null
    });
    const persistSpaceFree = freeMode === FREE_BOOKING_MODE.SPACE_ONLY;
    const amountPending = markAsPaid
      ? 0
      : chargeableVenueForSave <= 0
        ? 0
        : pendingManuallyEdited
          ? (parseMoney(detailsDraft.amountPending) ?? Math.max(0, roundMoney(totalAmount - paidAmount)))
          : Math.max(0, roundMoney(totalAmount - paidAmount));
    const paymentDateValue = detailsDraft.paymentDate?.trim() || null;
    const freeModeRequirements = buildFreeBookingModeRequirements(freeMode);

    const lastPricedSnapshot = persistSpaceFree
      ? resolveLastPricedSnapshotForFreeSave({
          requirements: reqBase,
          inMemoryRef: freeTogglePricingSnapshotRef.current,
          booking
        })
      : venueTotalForSave > 0
        ? lastPricedSnapshotFromDraftPricing({
            bookingAmount: detailsDraft.bookingAmount,
            addOnsAmount: detailsDraft.addOnsAmount,
            amountPaid: detailsDraft.amountPaid,
            amountPending: detailsDraft.amountPending,
            draftPackages
          })
        : parseLastPricedSnapshot(reqBase);

    const existingCustomerInfo =
      reqBase.customer_info && typeof reqBase.customer_info === 'object' ? reqBase.customer_info : {};
    const contactForSave = resolveBookingOrganizerContact(booking, bookingNotes[0]);
    const customerInfo = {
      ...existingCustomerInfo,
      ...(contactForSave.name ? { name: contactForSave.name } : {}),
      ...(contactForSave.email ? { email: contactForSave.email } : {}),
      ...(contactForSave.phone ? { phone: contactForSave.phone } : {})
    };
    const hasCustomerInfo = Object.keys(customerInfo).length > 0;
    const bookingTypeForSave =
      detailsDraft.bookingType || reqBase.booking_type || booking.booking_type || 'table';
    const bookingModeForSave = normalizeBookingModeApi(bookingTypeForSave);
    const pricingMeta = buildPricingMetadata({
      bookingType: bookingTypeForSave,
      headcount: attendees,
      duration: durationHours,
      perPersonRate:
        bookingModeForSave === 'per_person' ? resolveConfirmedPerPersonRate({ booking, req: reqBase }) : null,
      spaceHourlyRate:
        bookingModeForSave === 'exclusive' ? resolveConfirmedSpaceHourlyRate({ booking, req: reqBase }) : null,
      baseFee: baseAmountForSave
    });

    const payload = {
      booking_date: detailsDraft.eventDate || null,
      start_time: detailsDraft.eventStartTime || null,
      end_time: detailsDraft.eventEndTime || null,
      booking_type: bookingTypeForSave,
      booking_mode: bookingModeForSave,
      event_name: detailsDraft.eventTitle?.trim() || null,
      headcount: attendees,
      total_amount: totalAmount,
      final_amount: totalAmount,
      fee_breakdown: feeSnapshot,
      amount_paid: paidAmount,
      amount_pending: amountPending,
      add_ons_total: addOnsTotalValue,
      discount_amount: discountForSave,
      processing_fee: !feeBreakdownForSave ? 0 : feeBreakdownForSave.processingFee,
      packages: packagePayload,
      transaction_id: detailsDraft.transactionId?.trim() || null,
      payment_method: detailsDraft.paymentMethod?.trim() || null,
      ...(markAsPaid ? { payment_status: 'paid' } : {}),
      ...(durationHours > 0 ? { duration_hours: durationHours } : {}),
      ...(bookingModeForSave === 'per_person' && pricingMeta.per_person_rate != null
        ? { hourly_rate: pricingMeta.per_person_rate }
        : bookingModeForSave === 'exclusive' && pricingMeta.space_hourly_rate != null
          ? { hourly_rate: pricingMeta.space_hourly_rate }
          : {}),
      requirements: {
        ...reqBase,
        ...pricingMeta,
        ...(hasCustomerInfo ? { customer_info: customerInfo } : {}),
        activity: detailsDraft.activity?.trim() || null,
        booking_type: detailsDraft.bookingType || reqBase.booking_type || null,
        catering_preference: detailsDraft.catering?.trim() || null,
        ...(isInHouseCateringPreference(detailsDraft.catering)
          ? {
              has_dietary_restrictions: Boolean(detailsDraft.hasDietaryRestrictions),
              dietary_restrictions: detailsDraft.hasDietaryRestrictions
                ? detailsDraft.dietaryRestrictions?.trim() || null
                : null
            }
          : {
              has_dietary_restrictions: false,
              dietary_restrictions: null
            }),
        needs_av: Boolean(detailsDraft.needsAv),
        alcohol_consumption: detailsDraft.alcohol?.trim() || null,
        transaction_id: detailsDraft.transactionId?.trim() || null,
        payment_method: detailsDraft.paymentMethod?.trim() || null,
        payment_date: paymentDateValue,
        amount_paid: paidAmount,
        amount_pending: amountPending,
        booking_amount: venueTotalForSave,
        add_ons_total: addOnsTotalValue,
        discount_code: detailsDraft.discountCode?.trim() || null,
        discount_amount: discountForSave,
        ...freeModeRequirements,
        platform_fee: !feeBreakdownForSave ? 0 : feeBreakdownForSave.platformFee,
        processing_fee: !feeBreakdownForSave ? 0 : feeBreakdownForSave.processingFee,
        packages: packagePayload,
        ...(lastPricedSnapshot && lastPricedSnapshotHasCharge(lastPricedSnapshot)
          ? { last_priced_snapshot: lastPricedSnapshot }
          : {}),
        ...(durationHours > 0 ? { duration_hours: durationHours } : {}),
        ...(isTbcBooking || needsVenuePricingConfirmation(booking)
          ? { hide_venue_pricing: false, pricing_display: 'confirmed' }
          : {})
      }
    };
    delete payload.requirements.table_count;
    delete payload.requirements.headcount;

    const storedPackages = extractPackagesFromBooking(booking);
    const packagesChanged = !packagesEqual(storedPackages, draftPackages);
    const dirtyPayload = pickDirtyBookingUpdatePayload({
      fullPayload: payload,
      pendingChanges: pendingDraftChanges,
      packagesChanged,
      markAsPaid
    });
    if (!dirtyPayload || Object.keys(dirtyPayload).length === 0) {
      return;
    }
    if (!packagesChanged && Object.prototype.hasOwnProperty.call(dirtyPayload, 'packages')) {
      delete dirtyPayload.packages;
      if (dirtyPayload.requirements && typeof dirtyPayload.requirements === 'object') {
        delete dirtyPayload.requirements.packages;
        if (Object.keys(dirtyPayload.requirements).length === 0) {
          delete dirtyPayload.requirements;
        }
      }
    }

    const slotKeys = ['headcount', 'booking_date', 'start_time', 'end_time', 'booking_mode', 'space_id'];
    const touchesSlot = slotKeys.some((key) => Object.prototype.hasOwnProperty.call(dirtyPayload, key));
    if (touchesSlot && bookingVenueId) {
      const spaceId = booking.space_id || booking.spaceId;
      const date = dirtyPayload.booking_date || booking.booking_date;
      const start = dirtyPayload.start_time || booking.start_time;
      const end = dirtyPayload.end_time || booking.end_time;
      if (spaceId && date && start && end) {
        try {
          const preview = await venueDashboardApiService.getSpaceAvailabilityPreview(
            String(bookingVenueId),
            String(spaceId),
            {
              date: String(date).split('T')[0],
              startTime: String(start).length >= 5 ? String(start) : `${start}:00`,
              endTime: String(end).length >= 5 ? String(end) : `${end}:00`,
              bookingMode: bookingModeForSave,
              headcount: bookingModeForSave === 'per_person' ? attendees : undefined
            },
            { forceRefresh: true }
          );
          const total = Number(preview?.capacity_total);
          const used = Number(preview?.capacity_used);
          if (Number.isFinite(total) && Number.isFinite(used)) {
            const currentOverlaps = bookingWindowsOverlap(
              {
                space_id: booking.space_id || booking.spaceId,
                booking_date: booking.booking_date,
                start_time: booking.start_time,
                end_time: booking.end_time
              },
              { space_id: spaceId, booking_date: date, start_time: start, end_time: end }
            );
            const currentSeats =
              bookingModeForSave === 'exclusive' && String(booking.booking_mode || '').toLowerCase() === 'exclusive'
                ? total
                : (existingAttendees || 0);
            const othersSeats = currentOverlaps ? Math.max(0, used - currentSeats) : used;
            if (bookingModeForSave === 'exclusive' && othersSeats > 0) {
              throw new Error(
                'Exclusive hire is not available when another booking overlaps this time'
              );
            }
            if (bookingModeForSave === 'per_person') {
              const maxForThis = maxHeadcountAfterOthers(total, othersSeats);
              if (attendees > maxForThis) {
                throw new Error(
                  `Not enough capacity for the selected time. Maximum for this booking is ${maxForThis}.`
                );
              }
            }
          }
        } catch (previewError) {
          if (
            previewError?.message &&
            /already booked|not enough capacity|maximum for this booking/i.test(previewError.message)
          ) {
            throw previewError;
          }
        }
      }
    }

    try {
      await venueDashboardApiService.updateBooking(String(idForPut), dirtyPayload);
    } catch (error) {
      // Production booking_packages still rejects venue_items ids (clay, etc.).
      // Requirements.packages is enough to persist the qty until that API is deployed.
      if (!packagesChanged || !Object.prototype.hasOwnProperty.call(dirtyPayload, 'packages')) {
        throw error;
      }
      const retryPayload = { ...dirtyPayload };
      delete retryPayload.packages;
      await venueDashboardApiService.updateBooking(String(idForPut), retryPayload);
      // First PUT already wrote totals/add-ons then 500'd; the retry looks unchanged
      // so the API skips the update email. Send it here.
      try {
        await venueDashboardApiService.sendBookingUpdatedEmail(String(idForPut));
      } catch (emailError) {
        console.warn('Booking saved but update email was not sent', emailError);
      }
    }
    onBookingLocalUpdate?.(
      String(idForPut),
      mergeBookingLocalUpdate(
        { ...booking, requirements: parseBookingRequirements(booking) },
        {
          ...dirtyPayload,
          payment_status: markAsPaid ? 'paid' : booking.payment_status,
          paymentStatus: markAsPaid ? 'paid' : booking.paymentStatus,
          updated_at: paymentDateValue
            ? new Date(`${paymentDateValue}T12:00:00`).toISOString()
            : new Date().toISOString()
        }
      )
    );
    setDetailsDraft((prev) => ({
      ...prev,
      addOnsAmount: String(addOnsTotal),
      amountPending: pendingManuallyEdited ? prev.amountPending : String(amountPending)
    }));
    setDetailsDirty(false);
    setAddonsDirty(false);
    if (lastPricedSnapshot && lastPricedSnapshotHasCharge(lastPricedSnapshot)) {
      freeTogglePricingSnapshotRef.current = draftPricingFromLastPricedSnapshot(lastPricedSnapshot);
    }
    onBookingRefresh?.({ silent: true });
  };

  const handleEventTitleBlur = () => {
    const trimmed = String(detailsDraft.eventTitle || '').trim();
    const current = String(booking.event_name || booking.event || '').trim();
    if (!trimmed) {
      setDetailsDraft((prev) => ({ ...prev, eventTitle: current }));
      return;
    }
    if (trimmed !== detailsDraft.eventTitle) {
      setDetailsDraft((prev) => ({ ...prev, eventTitle: trimmed }));
    }
  };

  const switchDrawerTab = (nextTab) => {
    if (nextTab === modalTab) return;
    setModalTab(nextTab);
  };

  const finishDrawerNavigation = () => {
    if (mobileSection?.key && drawerNavigationIntentRef.current === 'back') {
      onMobileBack?.();
      return;
    }
    onClose();
  };

  const requestCloseDrawer = () => {
    drawerNavigationIntentRef.current = 'close';
    if (isBookingReadOnly) {
      finishDrawerNavigation();
      return;
    }
    if (isPaymentEditMode && !hasCloseableDraftChanges) {
      applyBookingToDraft(booking);
      setIsPaymentEditMode(false);
      setMarkAsPaidFlow(false);
      setDetailsSaveError('');
      return;
    }
    if (hasCloseableDraftChanges) {
      setClosePromptOpen(true);
      return;
    }
    finishDrawerNavigation();
  };

  const requestMobileBack = () => {
    drawerNavigationIntentRef.current = 'back';
    if (isBookingReadOnly) {
      onMobileBack?.();
      return;
    }
    if (hasCloseableDraftChanges) {
      setClosePromptOpen(true);
      return;
    }
    onMobileBack?.();
  };

  const handleClosePromptCancel = () => {
    if (closePromptBusy) return;
    setClosePromptOpen(false);
  };

  const handleClosePromptDiscard = () => {
    if (closePromptBusy) return;
    applyBookingToDraft(booking);
    setIsDetailsEditMode(false);
    setIsPaymentEditMode(false);
    setMarkAsPaidFlow(false);
    setClosePromptOpen(false);
    finishDrawerNavigation();
  };

  const handleClosePromptSave = async () => {
    if (closePromptBusy) return;
    setClosePromptBusy(true);
    setDetailsSaveError('');
    setAddonsSaveError('');
    try {
      await saveDraftBookingChanges();
      setClosePromptOpen(false);
      finishDrawerNavigation();
    } catch (error) {
      const message = error?.message || 'Failed to save changes.';
      if (modalTab === TAB_ADDONS) {
        setAddonsSaveError(message);
      } else {
        setDetailsSaveError(message);
      }
    } finally {
      setClosePromptBusy(false);
    }
  };

  const handleSaveAddons = async () => {
    if (isBookingReadOnly || !bookingIdResolved || !hasPendingDraftChanges) return;
    setAddonsSaving(true);
    setAddonsSaveError('');
    try {
      await saveDraftBookingChanges();
    } catch (e) {
      setAddonsSaveError(e?.message || 'Failed to save add-ons');
    } finally {
      setAddonsSaving(false);
    }
  };

  const handleSaveDetails = async () => {
    if (isBookingReadOnly || !hasPendingDraftChanges) return;
    setDetailsSaveError('');
    try {
      await saveDraftBookingChanges();
      setIsDetailsEditMode(false);
    } catch (error) {
      setDetailsSaveError(error?.message || 'Failed to save changes.');
    }
  };

  const handleCancelDetailsEdit = () => {
    applyBookingToDraft(booking);
    setIsDetailsEditMode(false);
    setDetailsSaveError('');
  };

  const handleCancelPaymentEdit = () => {
    applyBookingToDraft(booking);
    setIsPaymentEditMode(false);
    setMarkAsPaidFlow(false);
    setDetailsSaveError('');
  };

  const validatePaymentDetailsForSave = () => {
    const paid = parseMoney(detailsDraft.amountPaid);
    if (paid == null || Number.isNaN(paid) || paid < 0) {
      setDetailsSaveError('Enter a valid amount paid.');
      return false;
    }
    if (!detailsDraft.paymentMethod?.trim()) {
      setDetailsSaveError('Payment mode is required.');
      return false;
    }
    if (!detailsDraft.transactionId?.trim()) {
      setDetailsSaveError('Transaction ID is required.');
      return false;
    }
    if (!detailsDraft.paymentDate?.trim()) {
      setDetailsSaveError('Payment date is required.');
      return false;
    }
    return true;
  };

  const handleCancelPriceEdit = () => {
    if (!booking) {
      setIsPriceEditMode(false);
      setDetailsSaveError('');
      return;
    }
    const openedPricing = resolveOpenedBookingPricing(booking, {
      venueId: booking.venue_id ?? booking.venueId ?? venueId
    });
    const req = parseBookingRequirements(booking);
    setDetailsDraft((prev) => ({
      ...prev,
      bookingAmount: toNumericInputValue(
        openedPricing.snapshotDraft?.bookingAmount != null
          ? parseMoney(openedPricing.snapshotDraft.bookingAmount)
          : openedPricing.stuckFreeFlag
            ? openedPricing.restoredBaseAmount
            : openedPricing.resolvedBaseAmount
      ),
      discountCode: String(booking.discount_code ?? req.discount_code ?? '').trim(),
      discountAmount: toNumericInputValue(
        Number.parseFloat(booking.discount_amount ?? req.discount_amount ?? 0) || 0
      ),
      freeBookingMode: openedPricing.freeBookingMode
    }));
    setBookingAmountManuallyEdited(false);
    setIsPriceEditMode(false);
    setDetailsSaveError('');
  };

  const handleSavePriceChanges = async () => {
    if (isBookingReadOnly || !hasRightPanelPriceDraftChanges) {
      setIsPriceEditMode(false);
      return;
    }
    setDetailsSaveError('');
    try {
      await saveDraftBookingChanges();
      setIsPriceEditMode(false);
    } catch (error) {
      setDetailsSaveError(error?.message || 'Failed to save price changes.');
    }
  };

  const handleSavePaymentChanges = async () => {
    if (isBookingReadOnly) return;
    setDetailsSaveError('');
    if (!validatePaymentDetailsForSave()) return;

    setIsConfirming(true);
    try {
      await saveDraftBookingChanges({ markAsPaid: markAsPaidFlow });
      if (markAsPaidFlow) {
        setReminderSuccess('Booking marked as paid.');
      }
      setMarkAsPaidFlow(false);
      setIsPaymentEditMode(false);
    } catch (error) {
      setDetailsSaveError(error?.message || 'Failed to save payment changes.');
    } finally {
      setIsConfirming(false);
    }
  };

  const runBookingConfirmation = async (
    nextBookingStatus,
    nextPaymentStatus = null,
    { saveDraftFirst = false } = {}
  ) => {
    const bookingId = booking.id ?? booking.booking_id ?? bookingIdResolved;
    if (!bookingId) return;

    setIsConfirming(true);
    setDetailsSaveError('');
    try {
      if (saveDraftFirst && hasPendingDraftChanges) {
        await saveDraftBookingChanges();
      }
      if (onUpdateBookingStatus && (nextBookingStatus || nextPaymentStatus || paymentStatusAction)) {
        const effectivePaymentAction = nextPaymentStatus || paymentStatusAction || null;
        const statusResult = await onUpdateBookingStatus(
          bookingId,
          nextBookingStatus || null,
          customerMessage,
          effectivePaymentAction,
          effectiveBookingForPayment || booking
        );
        if (statusResult?.ok === false) {
          const failureMessage =
            statusResult.errorMessage || 'Unable to update booking. Please try again in a moment.';
          window.alert(failureMessage);
          onBookingRefresh?.({ silent: true });
          onClose();
          return;
        }
        if (nextBookingStatus === 'confirmed') {
          if (statusResult?.emailSent) {
            setApprovalFeedback(paymentEmailFeedbackMessage(statusResult?.paymentMode));
          } else if (statusResult?.emailError) {
            setApprovalFeedback(
              `Booking accepted, but the confirmation email could not be sent: ${statusResult.emailError} Use Send payment reminder email in Payment details.`
            );
            setIsConfirming(false);
            return;
          } else if (effectivePaymentAction === 'completed') {
            setApprovalFeedback(
              'Booking marked as accepted and paid — no Stripe payment link was sent.'
            );
          }
        } else if (nextBookingStatus === 'canceled_by_owner') {
          if (statusResult?.emailSent) {
            setApprovalFeedback('Booking cancelled. The customer has been notified by email.');
          } else if (statusResult?.emailError) {
            setApprovalFeedback(
              `Booking cancelled, but the notification email could not be sent: ${statusResult.emailError}`
            );
            setIsConfirming(false);
            return;
          } else {
            setApprovalFeedback('Booking cancelled.');
          }
        }
      }

      if (customerMessage.trim() && messageSenderId) {
        try {
          const existingThreads = await messageThreadsApiService.listThreads({
            userId: messageSenderId,
            bookingId,
            limit: 1
          });

          let threadId = existingThreads[0]?.id || null;

          if (!threadId) {
            const created = await messageThreadsApiService.createThread({
              venueId: booking.venue_id || booking.venueId,
              topic: buildBookingThreadTopic({
                bookingReference: booking.booking_reference || booking.bookingReference,
                eventName: booking.event_name || booking.eventName,
                organizerName:
                  organizerContact.name ||
                  booking.contact_name ||
                  booking.customer_name ||
                  booking.organizer_name ||
                  parseBookingRequirements(booking)?.customer_info?.name,
                venueName: booking.venue_name || booking.venueName
              }),
              bookingId,
              createdByUserId: messageSenderId,
              creatorRole: 'owner',
              participants: organizerUserId && organizerUserId !== messageSenderId
                ? [{ user_id: organizerUserId, role: 'organiser' }]
                : [],
              organizerEmail: guestOrganizerEmail || undefined
            });
            threadId = extractThreadId(created);
          }

          if (threadId) {
            await messageThreadsApiService.sendMessage(threadId, customerMessage, {
              userId: messageSenderId,
              ownerId: messageSenderId,
              senderId: messageSenderId,
              receiverId: organizerReceiverId
            });
          }
        } catch {
          // Non-blocking
        }
      }

      if (nextBookingStatus) {
        onClose();
      } else {
        setPaymentStatusAction('');
        setIsPaymentEditMode(false);
        onBookingRefresh?.({ silent: true });
      }
    } catch (error) {
      setDetailsSaveError(error?.message || 'Failed to save booking changes.');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleMarkPaidOutside = () => {
    if (isConfirming) return;
    const paidTotal = displayGrandTotal ?? chargeTotalNumber;
    const todayYmd = venueTimezone
      ? getYmdInTimezone(new Date(), venueTimezone)
      : formatLocalCalendarYmd(new Date());
    setDetailsDraft((prev) => ({
      ...prev,
      amountPaid: toNumericInputValue(paidTotal),
      amountPending: '0',
      paymentMethod: prev.paymentMethod?.trim() || 'Bank transfer',
      transactionId: prev.transactionId || '',
      paymentDate: todayYmd
    }));
    setPendingManuallyEdited(true);
    setMarkAsPaidFlow(true);
    setPriceDetailsTab(PRICE_TAB_PAYMENT);
    setIsPaymentEditMode(true);
    setDetailsSaveError('');
    setReminderSuccess('');
    setReminderError('');
  };

  const handleEnquiryAction = (status) => {
    if (isConfirming || addonsSaving) return;
    if (status === 'canceled_by_owner') {
      if (isPastBookingDate(booking)) return;
      setCancelConfirmOpen(true);
      return;
    }
    if (
      status === 'confirmed' &&
      isTbcBooking &&
      detailsDraft.freeBookingMode === FREE_BOOKING_MODE.NONE &&
      chargeableVenueSubtotal <= 0
    ) {
      setDetailsSaveError('Set a booking price or choose a free option before accepting.');
      return;
    }
    if (hasPendingDraftChanges && status !== 'confirmed') {
      setDetailsSaveError('Save or discard your edits before responding to this enquiry.');
      return;
    }
    const hasUserInitiatedDraftEdits =
      detailsDirty ||
      addonsDirty ||
      bookingAmountManuallyEdited ||
      pendingManuallyEdited ||
      pendingDraftChanges.some((change) => !PAYMENT_DRAFT_CHANGE_LABELS.has(change.label));
    setBookingStatusAction(status);
    runBookingConfirmation(status, null, {
      saveDraftFirst: status === 'confirmed' && hasUserInitiatedDraftEdits
    });
  };

  const handleConfirmCancelBooking = async () => {
    if (isConfirming || addonsSaving) return;
    if (isPastBookingDate(booking)) return;
    if (hasPendingDraftChanges) {
      setDetailsSaveError('Save or discard your edits before cancelling this booking.');
      return;
    }
    setCancelConfirmOpen(false);
    setBookingStatusAction('canceled_by_owner');
    await runBookingConfirmation('canceled_by_owner', null, { saveDraftFirst: false });
  };

  const handleStartOrganizerMessage = async () => {
    if (openingMessageThread || isConfirming || addonsSaving) return;
    if (!bookingVenueId || !bookingIdResolved || !messageSenderId) {
      setDetailsSaveError('Unable to start a message for this booking.');
      return;
    }

    setOpeningMessageThread(true);
    setDetailsSaveError('');
    try {
      const matchBookingThread = (thread) => {
        const threadBookingId = thread?.booking_id || thread?.bookingId || '';
        return String(threadBookingId) === String(bookingIdResolved);
      };

      let bookingThreads = [];
      try {
        bookingThreads = await messageThreadsApiService.listThreads({
          userId: messageSenderId,
          bookingId: bookingIdResolved,
          status: '',
          limit: 20
        });
      } catch {
        bookingThreads = [];
      }

      if (!bookingThreads.some(matchBookingThread)) {
        try {
          const venueThreads = await messageThreadsApiService.listVenueDashboardThreads({
            userId: messageSenderId,
            ownerId: messageSenderId,
            venueId: bookingVenueId,
            limit: 50
          });
          bookingThreads = messageThreadsApiService.mergeThreadsById(bookingThreads, venueThreads);
        } catch {
          // Keep the booking-scoped list if the venue inbox query fails.
        }
      }

      const existing = bookingThreads.find(matchBookingThread) || null;

      let threadId = existing?.id || null;
      if (!threadId) {
        const created = await messageThreadsApiService.createThread({
          venueId: bookingVenueId,
          topic: buildBookingThreadTopic({
            bookingReference: booking.booking_reference || booking.bookingReference,
            eventName: booking.event_name || booking.eventName,
            organizerName:
              organizerContact.name ||
              booking.contact_name ||
              booking.customer_name ||
              booking.organizer_name ||
              parseBookingRequirements(booking)?.customer_info?.name,
            venueName: booking.venue_name || booking.venueName
          }),
          bookingId: bookingIdResolved,
          createdByUserId: messageSenderId,
          creatorRole: 'owner',
          participants: organizerUserId && organizerUserId !== messageSenderId
            ? [{ user_id: organizerUserId, role: 'organiser' }]
            : [],
          organizerEmail: guestOrganizerEmail || undefined,
          organizerName: organizerContact.name || undefined
        });
        threadId = extractThreadId(created);
      }

      if (!threadId) {
        throw new Error('Unable to open a message thread for this organiser.');
      }

      if (onOpenMessages) {
        onOpenMessages({ threadId, bookingId: bookingIdResolved });
        onClose();
        return;
      }

      setDetailsSaveError('Open Messages in the sidebar to continue this conversation.');
    } catch (error) {
      setDetailsSaveError(error?.message || 'Failed to start a message with the organiser.');
    } finally {
      setOpeningMessageThread(false);
    }
  };

  if (autoStartMessage && !autoMessageStartedRef.current) {
    autoMessageStartedRef.current = true;
    queueMicrotask(() => {
      handleStartOrganizerMessage();
    });
  }

  const bookingReferenceLabel =
    booking.booking_reference || booking.id?.slice(-8) || '—';
  const bookingCreatedLabel = booking.created_at
    ? formatDate(new Date(booking.created_at), { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';
  const normalizedBookingStatus = String(bookingStatus || 'pending').toLowerCase();
  const bookingStatusLabel =
    bookingStatusLabels[normalizedBookingStatus] ||
    bookingStatusLabels[bookingStatus] ||
    'Pending';
  const bookingStatusMetaClass =
    normalizedBookingStatus === 'pending' || normalizedBookingStatus === 'on_hold'
      ? 'manage-drawer-meta-value--status-pending'
      : normalizedBookingStatus === 'confirmed' || normalizedBookingStatus === 'completed'
        ? 'manage-drawer-meta-value--status-confirmed'
        : normalizedBookingStatus.includes('cancel')
          ? 'manage-drawer-meta-value--status-rejected'
          : '';
  const showEnquiryFooter =
    (normalizedBookingStatus === 'pending' || normalizedBookingStatus === 'on_hold') &&
    !isPastBookingDate(booking);
  const isCancelledBooking = CANCELLED_BOOKING_STATUSES.has(normalizedBookingStatus);
  const isCurrentOrFutureBooking = !isPastBookingDate(booking);
  const canCancelBooking = !isCancelledBooking && isCurrentOrFutureBooking;
  const canMessageOrganizer = Boolean(bookingVenueId && bookingIdResolved && messageSenderId);
  const paymentDetailsAvailable = isApprovedBookingStatus(bookingStatus) || canEditTbcPricing;
  const paymentStatusLabel = paymentLabels[paymentStr] || paymentLabels[paymentRaw] || 'Pending';
  const paymentStatusBadgeClass =
    paymentClass === 'completed'
      ? 'manage-drawer-payment-status-badge--paid'
      : paymentClass === 'refunded'
        ? 'manage-drawer-payment-status-badge--refunded'
        : 'manage-drawer-payment-status-badge--pending';
  const paymentDateLabel = (() => {
    const storedDate = toDateInputValue(req.payment_date ?? booking.payment_date ?? detailsDraft.paymentDate);
    if (storedDate) {
      const parsed = parseDateOnly(storedDate);
      if (parsed && !Number.isNaN(parsed.getTime())) {
        return formatDate(parsed, { day: 'numeric', month: 'long', year: 'numeric' });
      }
    }
    if (paymentIsSettled && booking.updated_at) {
      return formatDate(new Date(booking.updated_at), { day: 'numeric', month: 'long', year: 'numeric' });
    }
    return '—';
  })();
  const headerPaymentStatusLabel = paymentIsSettled && booking.updated_at
    ? `Paid on ${formatDate(new Date(booking.updated_at), { day: 'numeric', month: 'long', year: 'numeric' })}`
    : null;
  const showPriceBreakdownEdit = canEditPricingFields || isPriceEditMode;
  const bookingApprovedAndPaid =
    isApprovedBookingStatus(bookingStatus) && bookingIsPaid;
  const canOpenPriceEdit =
    paymentDetailsAvailable &&
    !canEditTbcPricing &&
    !isPriceEditMode &&
    showFeeBreakdown &&
    !bookingApprovedAndPaid &&
    !isPastBooking;
  const canOpenPaymentEdit =
    paymentDetailsAvailable &&
    !canEditTbcPricing &&
    !isPaymentEditMode &&
    !showPaymentCollectionPanel &&
    !(bookingIsFree && bookingIsPaid) &&
    !bookingIsPaid &&
    !isPastBooking;

  const renderPriceBreakdownContent = () => {
    if (!showFeeBreakdown) {
      return <p className="manage-drawer-inline-hint">No charge on this booking.</p>;
    }

    return (
      <>
        {canEditTbcPricing ? (
          <p className="manage-drawer-inline-hint manage-drawer-tbc-pricing-hint">
            {hasListedSpaceRate
              ? 'Set pricing before accepting. Space fee recalculates when guests or booking type change. Add-ons are edited on the Add-ons tab.'
              : 'Set the space fee before accepting. Guest count does not change a custom price. Add-ons are edited on the Add-ons tab.'}
          </p>
        ) : null}
        {showPriceBreakdownEdit ? (
          <div className="manage-drawer-price-field">
            <label className="manage-drawer-price-field-label" htmlFor="manage-drawer-space-fee">
              Space fee<span className="manage-drawer-required">*</span>
            </label>
            <input
              id="manage-drawer-space-fee"
              type="number"
              min="0"
              step="0.01"
              className="manage-drawer-price-field-input"
              value={detailsDraft.bookingAmount}
              onChange={(e) => updateDetailsDraft('bookingAmount', e.target.value)}
              aria-label="Space fee"
            />
          </div>
        ) : (
          <div className="manage-drawer-price-row">
            <div>
              <p className="manage-drawer-price-row-label">Space fee</p>
              {pricingBreakdown.detail ? (
                <p className="manage-drawer-price-row-detail">{pricingBreakdown.detail}</p>
              ) : null}
            </div>
            <span className="manage-drawer-price-row-amount">
              {bookingIsSpaceFree ? (
                <>
                  {displayBaseAmount > 0 ? (
                    <>
                      <span className="manage-drawer-price-row-amount--struck">{formatCurrency(displayBaseAmount)}</span>
                      {' '}
                    </>
                  ) : null}
                  Free
                </>
              ) : (
                formatCurrency(displayBaseAmount)
              )}
            </span>
          </div>
        )}
        {showPriceBreakdownEdit && !canEditTbcPricing ? (
          <p className="manage-drawer-price-noneditable-hint">
            The price elements below are not editable.
          </p>
        ) : null}
        {sidebarAddonLines.length > 0 || canEditTbcPricing ? (
          <>
            <div className="manage-drawer-price-row">
              <p className="manage-drawer-price-row-label">Add-ons</p>
              <span className="manage-drawer-price-row-amount">{formatCurrency(displayAddOnsTotal)}</span>
            </div>
            {sidebarAddonLines.length > 0 ? (
              <div className="manage-drawer-price-addon-list">
                {sidebarAddonLines.map((line) => (
                  <div className="manage-drawer-price-addon-item" key={line._key || line.package_id}>
                    <span className="manage-drawer-price-addon-name">
                      {line.name}
                      {(line.quantity ?? 0) > 0 ? ` × ${line.quantity}` : ''}
                    </span>
                    <span className="manage-drawer-price-row-amount">{formatCurrency(line.total)}</span>
                  </div>
                ))}
              </div>
            ) : canEditTbcPricing ? (
              <p className="manage-drawer-inline-hint manage-drawer-price-addon-hint">
                No add-ons yet — use the Add-ons tab to include items.
              </p>
            ) : null}
          </>
        ) : null}
        {showPriceBreakdownEdit && (canEditPricingFields || discountAmountNumber > 0) ? (
          <div className="manage-drawer-price-row manage-drawer-price-row--discount">
            <p className="manage-drawer-price-row-label">Discount</p>
            {canEditPricingFields ? (
              <div className="manage-drawer-price-discount-fields">
                <input
                  type="text"
                  className="manage-drawer-price-row-input manage-drawer-price-row-input--code"
                  value={detailsDraft.discountCode}
                  onChange={(e) => updateDetailsDraft('discountCode', e.target.value)}
                  placeholder="Discount code"
                />
                <button
                  type="button"
                  className="manage-drawer-price-discount-apply"
                  disabled={discountValidating}
                  onClick={handleApplyDiscountCode}
                >
                  {discountValidating ? 'Checking…' : 'Apply'}
                </button>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="manage-drawer-price-row-input"
                  value={detailsDraft.discountAmount}
                  onChange={(e) => updateDetailsDraft('discountAmount', e.target.value)}
                  aria-label="Discount amount"
                />
              </div>
            ) : (
              <span className="manage-drawer-price-row-amount manage-drawer-row-value--discount">
                −{formatCurrency(discountAmountNumber)}
              </span>
            )}
          </div>
        ) : discountAmountNumber > 0 ? (
          <div className="manage-drawer-price-row">
            <p className="manage-drawer-price-row-label">Discount</p>
            <span className="manage-drawer-price-row-amount manage-drawer-row-value--discount">
              −{formatCurrency(discountAmountNumber)}
            </span>
          </div>
        ) : null}
        {discountError ? <p className="manage-drawer-invoice-error">{discountError}</p> : null}
        <div className="manage-drawer-price-row manage-drawer-price-row--platform">
          <p className="manage-drawer-price-row-label">
            <span className="manage-drawer-price-platform-label">
              Platform fee
              <SidebarChevron open={false} />
            </span>
          </p>
          <span className="manage-drawer-price-row-amount">{formatCurrency(displayPlatformFee)}</span>
        </div>
        {chargeableVenueSubtotal > 0 ? (
          <div className="manage-drawer-price-row">
            <p className="manage-drawer-price-row-label">Processing fee</p>
            <span className="manage-drawer-price-row-amount">{formatCurrency(displayProcessingFee)}</span>
          </div>
        ) : null}
        <div className="manage-drawer-price-divider" />
        <div className="manage-drawer-price-total-row">
          <span className="manage-drawer-price-total-label">Amount payable</span>
          <span className="manage-drawer-price-total-value">{formatCurrency(displayGrandTotal)}</span>
        </div>
      </>
    );
  };

  const renderPaymentDetailsViewRows = () => (
    <div className="manage-drawer-payment-view-rows">
      <div className="manage-drawer-payment-view-row">
        <span className="manage-drawer-payment-view-label">Payment status</span>
        <span className={`manage-drawer-payment-status-badge ${paymentStatusBadgeClass}`}>
          {paymentStatusLabel}
        </span>
      </div>
      <div className="manage-drawer-payment-view-row">
        <span className="manage-drawer-payment-view-label">Payment date</span>
        <span className="manage-drawer-payment-view-value">{paymentDateLabel}</span>
      </div>
      <div className="manage-drawer-payment-view-row">
        <span className="manage-drawer-payment-view-label">Payment mode</span>
        <span className="manage-drawer-payment-view-value">{detailsDraft.paymentMethod || '—'}</span>
      </div>
      <div className="manage-drawer-payment-view-row">
        <span className="manage-drawer-payment-view-label">Transaction ID</span>
        <span className="manage-drawer-payment-view-value manage-drawer-payment-view-value--mono">
          {paymentDisplayFields.transactionId || '—'}
        </span>
      </div>
      <div className="manage-drawer-payment-view-row">
        <span className="manage-drawer-payment-view-label">Amount paid</span>
        <span className="manage-drawer-payment-view-value">
          {paymentClass === 'refunded'
            ? formatCurrency(0)
            : bookingIsPaid || amountPaidNumber > 0
              ? formatCurrency(
                  Math.max(parseMoney(detailsDraft.amountPaid) ?? 0, resolveBookingAmountPaid(booking))
                )
              : '—'}
        </span>
      </div>
      <div className="manage-drawer-payment-view-row">
        <span className="manage-drawer-payment-view-label">Amount pending</span>
        <span className="manage-drawer-payment-view-value">
          {bookingIsPaid
            ? '—'
            : displayAmountPending > 0
              ? formatCurrency(displayAmountPending)
              : '—'}
        </span>
      </div>
    </div>
  );

  const renderPaymentInvoiceField = () => (
    <div className="manage-drawer-invoice">
      <p className="manage-drawer-field-label">Invoice</p>
          <input
            ref={invoiceInputRef}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="manage-drawer-invoice-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              handleInvoiceUpload(file);
              event.target.value = '';
            }}
          />
          {invoiceLoading ? (
            <p className="manage-drawer-invoice-helper">Loading invoice...</p>
          ) : activeInvoice ? (
            <div className="manage-drawer-invoice-file">
              <div className="manage-drawer-invoice-file-left">
                <VenueDocumentIcon className="manage-drawer-invoice-file-icon" />
                {activeInvoice.id ? (
                  <button
                    type="button"
                    className="manage-drawer-invoice-file-name-btn"
                    onClick={() => handleInvoiceDownload(activeInvoice)}
                  >
                    {activeInvoiceName}
                  </button>
                ) : activeInvoice.url ? (
                  <a
                    className="manage-drawer-invoice-file-name"
                    href={activeInvoice.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {activeInvoiceName}
                  </a>
                ) : (
                  <span className="manage-drawer-invoice-file-name">{activeInvoiceName}</span>
                )}
              </div>
              {activeInvoice.id ? (
                <button
                  type="button"
                  className="manage-drawer-invoice-delete"
                  aria-label="Remove invoice"
                  onClick={handleInvoiceDelete}
                  disabled={invoiceUploading}
                >
                  <FigmaTrashIcon />
                </button>
              ) : null}
            </div>
          ) : (
            <label
              className="manage-drawer-invoice-upload"
              onClick={() => invoiceInputRef.current?.click()}
            >
              <span className="manage-drawer-invoice-upload-plus">+</span>
              <p className="manage-drawer-invoice-upload-text">
                {invoiceUploading ? 'Uploading...' : <>Drag and drop here or <span>choose file</span></>}
              </p>
            </label>
          )}
          {invoiceError ? <p className="manage-drawer-invoice-error">{invoiceError}</p> : null}
    </div>
  );

  const renderPaymentDetailsEditForm = () => (
    <div className="manage-drawer-payment-edit-form">
      {isTbcBooking ? (
        <p className="manage-drawer-inline-hint manage-drawer-tbc-pricing-hint">
          No price was shown to the organiser when they requested this booking. Set the space fee
          in Price breakdown, add add-ons on the Add-ons tab, upload an invoice, or mark it as a
          free booking.
        </p>
      ) : null}
      <div className="manage-drawer-payment-edit-field">
        <label className="manage-drawer-payment-edit-label" htmlFor="manage-drawer-payment-status">
          Payment status<span className="manage-drawer-required">*</span>
        </label>
        <input
          id="manage-drawer-payment-status"
          type="text"
          className="manage-drawer-payment-edit-input"
          value={markAsPaidFlow ? 'Paid' : paymentStatusLabel}
          readOnly
        />
      </div>
      <div className="manage-drawer-payment-edit-field">
        <label className="manage-drawer-payment-edit-label" htmlFor="manage-drawer-amount-paid">
          Amount paid<span className="manage-drawer-required">*</span>
        </label>
        <input
          id="manage-drawer-amount-paid"
          type="number"
          min="0"
          step="0.01"
          className="manage-drawer-payment-edit-input"
          value={detailsDraft.amountPaid}
          onChange={(e) => updateDetailsDraft('amountPaid', e.target.value)}
        />
      </div>
      <div className="manage-drawer-payment-edit-field">
        <label className="manage-drawer-payment-edit-label" htmlFor="manage-drawer-payment-date">
          Payment date<span className="manage-drawer-required">*</span>
        </label>
        <CustomDatePicker
          id="manage-drawer-payment-date"
          className="manage-drawer-payment-edit-date"
          value={detailsDraft.paymentDate}
          onChange={(date) => updateDetailsDraft('paymentDate', date)}
          placeholder="Select date"
        />
      </div>
      <div className="manage-drawer-payment-edit-field">
        <label className="manage-drawer-payment-edit-label" htmlFor="manage-drawer-payment-mode">
          Payment mode<span className="manage-drawer-required">*</span>
        </label>
        <select
          id="manage-drawer-payment-mode"
          className="manage-drawer-payment-edit-input"
          value={detailsDraft.paymentMethod || PAYMENT_MODE_OPTIONS[0]}
          onChange={(e) => updateDetailsDraft('paymentMethod', e.target.value)}
        >
          {detailsDraft.paymentMethod &&
          !PAYMENT_MODE_OPTIONS.includes(detailsDraft.paymentMethod) ? (
            <option value={detailsDraft.paymentMethod}>{detailsDraft.paymentMethod}</option>
          ) : null}
          {PAYMENT_MODE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
      <div className="manage-drawer-payment-edit-field">
        <label className="manage-drawer-payment-edit-label" htmlFor="manage-drawer-transaction-id">
          Transaction ID<span className="manage-drawer-required">*</span>
        </label>
        <input
          id="manage-drawer-transaction-id"
          type="text"
          className="manage-drawer-payment-edit-input manage-drawer-payment-edit-input--mono"
          value={detailsDraft.transactionId}
          onChange={(e) => updateDetailsDraft('transactionId', e.target.value)}
          placeholder="Transaction ID"
        />
      </div>
      {renderPaymentInvoiceField()}
      {!markAsPaidFlow ? (
        <>
      <div className="manage-drawer-row manage-drawer-row--free-booking">
        <span className="manage-drawer-row-label">Pricing waiver</span>
        <div className="manage-drawer-free-booking-options" role="radiogroup" aria-label="Pricing waiver">
          <label className="manage-drawer-free-booking-toggle">
            <input
              type="radio"
              name="freeBookingMode"
              checked={detailsDraft.freeBookingMode === FREE_BOOKING_MODE.NONE}
              disabled={bookingIsPaid}
              onChange={() => handleFreeBookingModeChange(FREE_BOOKING_MODE.NONE)}
            />
            <span className="manage-drawer-free-booking-toggle-text">
              Standard pricing — space and add-ons charged
            </span>
          </label>
          <label className="manage-drawer-free-booking-toggle">
            <input
              type="radio"
              name="freeBookingMode"
              checked={detailsDraft.freeBookingMode === FREE_BOOKING_MODE.SPACE_ONLY}
              disabled={bookingIsPaid}
              onChange={() => handleFreeBookingModeChange(FREE_BOOKING_MODE.SPACE_ONLY)}
            />
            <span className="manage-drawer-free-booking-toggle-text">
              Free space — add-ons still charged if any
            </span>
          </label>
        </div>
      </div>
      <div className="manage-drawer-payment-edit-field">
          <label className="manage-drawer-payment-edit-label" htmlFor="manage-drawer-amount-pending">
            Amount pending
          </label>
          <input
            id="manage-drawer-amount-pending"
            type="number"
            min="0"
            step="0.01"
            className="manage-drawer-payment-edit-input"
            value={detailsDraft.amountPending}
            onChange={(e) => updateDetailsDraft('amountPending', e.target.value)}
          />
        </div>
      {renderPaymentStatusUpdateSection()}
        </>
      ) : null}
    </div>
  );

  const onlinePaymentCopy = getOnlinePaymentOptionCopy(paymentContext?.mode || 'occasia_stripe');
  const sidebarAddonLines = draftPackages.filter((line) => line.quantity > 0);
  const drawerTabs = [
    { id: TAB_BOOKING, label: 'Event requirements', icon: 'requirements' },
    { id: TAB_ADDONS, label: 'Add-ons', icon: 'addons' },
    { id: TAB_NOTES, label: 'Notes', icon: 'notes' },
    { id: TAB_CHANGELOG, label: 'Change log', icon: 'changelog' }
  ];

  const isMobileSection = Boolean(mobileSection?.key);
  const showMobilePricePanel =
    isMobileSection &&
    (mobileSection.key === 'breakdown' || mobileSection.key === 'payment');
  const mobileSectionTitle =
    (mobileSection?.key && MOBILE_SECTION_TITLES[mobileSection.key]) || 'Booking';

  const openAddonPicker = () => {
    if (venueAddonCatalog.length === 0) return;
    const firstAvailable = availableCatalogForPicker[0];
    setAddonPickerSelection(
      firstAvailable ? String(firstAvailable.id || firstAvailable.item_id) : ''
    );
    setAddonPickerOpen(true);
  };

  const closeAddonPicker = () => {
    setAddonPickerOpen(false);
    setAddonPickerSelection('');
  };

  const confirmAddonPicker = () => {
    if (!addonPickerSelection) return;
    const item = venueAddonCatalog.find(
      (catalogItem) => String(catalogItem.id || catalogItem.item_id) === String(addonPickerSelection)
    );
    if (item) bumpCatalogAddonQty(item, 1);
    closeAddonPicker();
  };

  const renderAddonPickerTile = () => {
    if (!addonPickerOpen) return null;
    const hasOptions = availableCatalogForPicker.length > 0;

    return (
      <div className="manage-drawer-addon-picker-tile">
        <p className="manage-drawer-addon-picker-label">Add an item</p>
        {hasOptions ? (
          <select
            className="manage-drawer-addon-picker-select"
            value={addonPickerSelection}
            onChange={(e) => setAddonPickerSelection(e.target.value)}
          >
            {availableCatalogForPicker.map((item) => {
              const id = item.id || item.item_id;
              const label = item.name || item.item_name || 'Add-on';
              const unit =
                Number(item.price ?? item.price_per_unit ?? item.unit_price ?? 0) || 0;
              return (
                <option key={String(id)} value={String(id)}>
                  {label} — {formatUnitCurrency(unit)} / unit
                </option>
              );
            })}
          </select>
        ) : (
          <p className="manage-drawer-inline-hint manage-drawer-addon-picker-empty">
            All venue add-ons are already on this booking. Use + on a row to increase quantity.
          </p>
        )}
        <div className="manage-drawer-addon-picker-actions">
          <button
            type="button"
            className="manage-drawer-addon-picker-btn manage-drawer-addon-picker-btn--cancel"
            onClick={closeAddonPicker}
          >
            Cancel
          </button>
          <button
            type="button"
            className="manage-drawer-addon-picker-btn manage-drawer-addon-picker-btn--add"
            onClick={confirmAddonPicker}
            disabled={!hasOptions || !addonPickerSelection}
          >
            Add item
          </button>
        </div>
      </div>
    );
  };

  const renderStoredAddonReadOnlyRow = (line) => {
    const qty = line.quantity ?? 0;
    const lineTotal = line.total != null ? Number(line.total) : 0;
    const unit =
      line.unit_price != null
        ? Number(line.unit_price) || 0
        : qty > 0
          ? roundMoney(lineTotal / qty)
          : 0;

    return (
      <div className="manage-drawer-table-data-row" key={line._key || String(line.package_id ?? line.name)}>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--item" title={line.name}>
          {line.name}
        </div>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--price">
          {formatUnitCurrency(unit)}
        </div>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--qty">
          <span className="manage-drawer-addon-qty-display">{qty}</span>
        </div>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--total">
          <span className="manage-drawer-addon-line-total">{formatCurrency(lineTotal)}</span>
        </div>
      </div>
    );
  };

  const renderAddonTableRow = (item, { orphan = false, readOnly = false } = {}) => {
    const id = item.id || item.item_id || item.package_id;
    const draftLine = orphan
      ? findOrphanDraftLine(item, draftPackages) ?? item
      : draftPackages.find((p) => String(p.package_id) === String(id));
    const qty = draftLine?.quantity ?? item.quantity ?? 0;
    const catalogUnit =
      Number(item.price ?? item.price_per_unit ?? item.unit_price ?? 0) || 0;
    const draftTotal = draftLine && draftLine.total != null ? Number(draftLine.total) : null;
    const derivedDraftUnit =
      draftTotal != null && Number.isFinite(draftTotal) && qty > 0 ? draftTotal / qty : null;
    const bookingUnit =
      derivedDraftUnit != null
        ? derivedDraftUnit
        : draftLine && draftLine.unit_price != null
          ? Number(draftLine.unit_price) || 0
          : null;
    const unitForDisplay =
      bookingUnit != null && bookingUnit > 0
        ? bookingUnit
        : !isTbcBooking && catalogUnit > 0
          ? catalogUnit
          : bookingUnit != null
            ? bookingUnit
            : readOnly
              ? 0
              : catalogUnit;
    const lineTotal =
      draftLine?.total != null && Number(draftLine.total) > 0
        ? Number(draftLine.total)
        : roundMoney(unitForDisplay * qty);
    const name = item.name || item.item_name || draftLine?.name || 'Add-on';
    const bump = orphan
      ? (delta) => bumpOrphanAddonQty(item, delta)
      : (delta) => bumpCatalogAddonQty(item, delta);

    return (
      <div className="manage-drawer-table-data-row" key={item._key || String(id)}>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--item" title={name}>
          {name}
        </div>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--price">
          {formatUnitCurrency(unitForDisplay)} {getAddonUnitLabel(item)}
        </div>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--qty">
          {readOnly ? (
            <span className="manage-drawer-addon-qty-display">{qty}</span>
          ) : (
          <div className="manage-drawer-addon-stepper">
            <button
              type="button"
              className="manage-drawer-addon-pill manage-drawer-addon-pill--minus"
              disabled={qty <= 0}
              onClick={() => bump(-1)}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <span className="manage-drawer-addon-qty-display">{qty}</span>
            <button
              type="button"
              className="manage-drawer-addon-pill manage-drawer-addon-pill--plus"
              onClick={() => bump(1)}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
          )}
        </div>
        <div className="manage-drawer-table-data-cell manage-drawer-table-data-cell--total">
          <div className="manage-drawer-addon-total-cell">
            {!readOnly && orphan ? (
              <button
                type="button"
                className="manage-drawer-addon-remove-inline"
                onClick={() => removeOrphanAddon(item)}
                aria-label="Remove add-on"
              >
                <FigmaTrashIcon />
              </button>
            ) : null}
            <span className="manage-drawer-addon-line-total">{formatCurrency(lineTotal)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`manage-drawer-overlay${isMobileSection ? ' manage-drawer-overlay--mobile-section' : ''}`}
      role="dialog"
      aria-modal="true"
      onClick={(e) => !isMobileSection && e.target === e.currentTarget && requestCloseDrawer()}
    >
      <div className={`manage-drawer${isMobileSection ? ' manage-drawer--mobile-section' : ''}`}>
        {isMobileSection ? (
          <header className="manage-drawer-mobile-bar">
            <button
              type="button"
              className="manage-drawer-mobile-icon-btn"
              aria-label="Back"
              onClick={requestMobileBack}
            >
              <img src={mobileBackIcon} alt="" width={20} height={20} className="is-back" />
            </button>
            <h2 className="manage-drawer-mobile-title">{mobileSectionTitle}</h2>
            <button
              type="button"
              className="manage-drawer-mobile-icon-btn"
              aria-label="Close"
              onClick={requestCloseDrawer}
            >
              <img src={mobileCloseIcon} alt="" width={20} height={20} />
            </button>
          </header>
        ) : null}
        {!isMobileSection ? (
        <div className="manage-drawer-header">
          <div className="manage-drawer-header-top">
            <input
              type="text"
              className="manage-drawer-title-input"
              value={detailsDraft.eventTitle}
              onChange={(e) => updateDetailsDraft('eventTitle', e.target.value)}
              onBlur={handleEventTitleBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              placeholder="Booking title"
              aria-label="Booking title"
              readOnly={isBookingReadOnly}
              disabled={isBookingReadOnly}
            />
            <button
              type="button"
              className="manage-drawer-close"
              onClick={requestCloseDrawer}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.17 14.83L14.83 9.17M14.83 14.83L9.17 9.17M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="manage-drawer-meta-grid">
            <div className="manage-drawer-meta-row">
              <div className="manage-drawer-meta-item">
                <span className="manage-drawer-meta-label">Booking ID</span>
                <span className="manage-drawer-meta-value">{bookingReferenceLabel}</span>
              </div>
              <div className="manage-drawer-meta-item">
                <span className="manage-drawer-meta-label">Booking date</span>
                <span className="manage-drawer-meta-value">{bookingCreatedLabel}</span>
              </div>
              <div className="manage-drawer-meta-item">
                <span className="manage-drawer-meta-label">Booking status</span>
                <span className={`manage-drawer-meta-value ${bookingStatusMetaClass}`}>
                  {bookingStatusLabel}
                </span>
              </div>
              {headerPaymentStatusLabel ? (
                <div className="manage-drawer-meta-item">
                  <span className="manage-drawer-meta-label">Payment status</span>
                  <span className="manage-drawer-meta-value manage-drawer-meta-value--status-confirmed">
                    {headerPaymentStatusLabel}
                  </span>
                </div>
              ) : null}
            </div>
            <div className="manage-drawer-meta-row">
              <div className="manage-drawer-meta-item">
                <span className="manage-drawer-meta-label">Customer name</span>
                <span className="manage-drawer-meta-value">
                  {organizerContact.name || '—'}
                </span>
              </div>
              <div className="manage-drawer-meta-item">
                <span className="manage-drawer-meta-label">Email</span>
                <span className="manage-drawer-meta-value">
                  {organizerContact.email || '—'}
                </span>
              </div>
              <div className="manage-drawer-meta-item">
                <span className="manage-drawer-meta-label">Contact</span>
                <span className="manage-drawer-meta-value">{contactNumber || '—'}</span>
              </div>
            </div>
            {adminOrganizerProfilePath ? (
              <div className="manage-drawer-admin-organizer-links">
                <a
                  href={adminOrganizerProfilePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="manage-drawer-admin-organizer-link"
                >
                  View organiser profile →
                </a>
                {adminOrganizerBookingPath ? (
                  <a
                    href={adminOrganizerBookingPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="manage-drawer-admin-organizer-link"
                  >
                    View booking as organiser →
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        ) : null}

        {!isMobileSection ? <div className="manage-drawer-header-divider" /> : null}

        <div className={`manage-drawer-split${isMobileSection ? ' manage-drawer-split--mobile-section' : ''}`}>
          {!showMobilePricePanel ? (
          <div className="manage-drawer-main">
            {isBookingReadOnly ? (
              <p className="manage-drawer-inline-hint manage-drawer-readonly-banner">
                This booking is in the past. Prices reflect what was confirmed at booking time and cannot be edited.
              </p>
            ) : null}
            {!isMobileSection ? (
            <div className="manage-drawer-pill-tabs">
              {drawerTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`manage-drawer-pill-tab ${modalTab === tab.id ? 'active' : ''}`}
                  onClick={() => switchDrawerTab(tab.id)}
                >
                  <DrawerTabIcon name={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>
            ) : null}

            {modalTab === TAB_BOOKING && !isBookingReadOnly && !isMobileSection ? (
              <div className="manage-drawer-edit-row">
                <button
                  type="button"
                  className="manage-drawer-edit-btn"
                  onClick={() => {
                    if (isDetailsEditMode) {
                      handleCancelDetailsEdit();
                    } else {
                      setIsDetailsEditMode(true);
                    }
                  }}
                >
                  {isDetailsEditMode ? 'Cancel edit' : 'Edit'}
                </button>
              </div>
            ) : null}

            <div className="manage-drawer-main-scroll">
          {modalTab === TAB_BOOKING && (
            <>
              {detailsSaveError ? (
                <p className="manage-drawer-invoice-error">{detailsSaveError}</p>
              ) : null}
              <div className="manage-drawer-table manage-drawer-table--requirements">
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Event date</div>
                  <div className="manage-drawer-table-value">
                    {isDetailsEditMode ? (
                      <CustomDatePicker
                        className="manage-drawer-row-date-picker"
                        value={detailsDraft.eventDate}
                        onChange={(date) => updateDetailsDraft('eventDate', date)}
                        placeholder="Select date"
                      />
                    ) : (
                      bookingDateLabel
                    )}
                  </div>
                </div>
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Event time</div>
                  {isDetailsEditMode ? (
                    <div className="manage-drawer-table-value manage-drawer-table-value--time">
                      <TimeRangePicker
                        startTime={detailsDraft.eventStartTime}
                        endTime={detailsDraft.eventEndTime}
                        onStartChange={(time) => updateDetailsDraft('eventStartTime', time)}
                        onEndChange={(time) => updateDetailsDraft('eventEndTime', time)}
                        startPlaceholder="Start"
                        endPlaceholder="End"
                        minEndTime={getMinEndTime(detailsDraft.eventStartTime)}
                        endDisabled={!detailsDraft.eventStartTime}
                        dropdownBelow
                      />
                    </div>
                  ) : (
                    <div className="manage-drawer-table-value">{timeRangeLabel}</div>
                  )}
                </div>
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Booking type</div>
                  <div className="manage-drawer-table-value">
                    {isDetailsEditMode ? (
                      <select
                        className="manage-drawer-row-input"
                        value={detailsDraft.bookingType}
                        onChange={(e) => updateDetailsDraft('bookingType', e.target.value)}
                      >
                        {showPerPersonBookingOption ? (
                          <option value="table">Per person</option>
                        ) : null}
                        <option value="full-venue">Full venue hire</option>
                      </select>
                    ) : (
                      bookingTypeLabel
                    )}
                  </div>
                </div>
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Activity</div>
                  <div className="manage-drawer-table-value">
                    {isDetailsEditMode ? (
                      <input
                        type="text"
                        className="manage-drawer-row-input"
                        value={detailsDraft.activity}
                        onChange={(e) => updateDetailsDraft('activity', e.target.value)}
                        placeholder="Activity"
                      />
                    ) : (
                      detailsDraft.activity || '—'
                    )}
                  </div>
                </div>
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Attendees</div>
                  <div className="manage-drawer-table-value">
                    {isDetailsEditMode ? (
                      <input
                        type="number"
                        min="0"
                        className="manage-drawer-row-input"
                        value={detailsDraft.attendees}
                        onChange={(e) => updateDetailsDraft('attendees', e.target.value)}
                      />
                    ) : (
                      detailsDraft.attendees || '—'
                    )}
                  </div>
                </div>
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Catering</div>
                  <div className="manage-drawer-table-value">
                    {isDetailsEditMode ? (
                      <input
                        type="text"
                        className="manage-drawer-row-input"
                        value={detailsDraft.catering}
                        onChange={(e) => updateDetailsDraft('catering', e.target.value)}
                        placeholder="Catering"
                      />
                    ) : (
                      detailsDraft.catering || '—'
                    )}
                  </div>
                </div>
                {isInHouseCateringPreference(detailsDraft.catering) ||
                detailsDraft.hasDietaryRestrictions ||
                detailsDraft.dietaryRestrictions ? (
                  <div className="manage-drawer-table-row">
                    <div className="manage-drawer-table-label">Dietary restrictions</div>
                    <div className="manage-drawer-table-value">
                      {isDetailsEditMode ? (
                        <div className="manage-drawer-dietary-editor">
                          {isInHouseCateringPreference(detailsDraft.catering) ? (
                            <label className="manage-drawer-checkbox-label">
                              <input
                                type="checkbox"
                                checked={Boolean(detailsDraft.hasDietaryRestrictions)}
                                onChange={(e) => updateDetailsDraft('hasDietaryRestrictions', e.target.checked)}
                              />
                              Guest dietary restrictions
                            </label>
                          ) : null}
                          {detailsDraft.hasDietaryRestrictions ? (
                            <textarea
                              className="manage-drawer-row-textarea"
                              value={detailsDraft.dietaryRestrictions}
                              onChange={(e) => updateDetailsDraft('dietaryRestrictions', e.target.value)}
                              placeholder="Share dietary restrictions"
                              rows={3}
                            />
                          ) : (
                            <span>None specified</span>
                          )}
                        </div>
                      ) : (
                        formatBookingDietaryRestrictions({
                          requirements: {
                            catering_preference: detailsDraft.catering,
                            has_dietary_restrictions: detailsDraft.hasDietaryRestrictions,
                            dietary_restrictions: detailsDraft.dietaryRestrictions
                          }
                        })
                      )}
                    </div>
                  </div>
                ) : null}
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Audio/visual (A/V)</div>
                  <div className="manage-drawer-table-value">
                    {isDetailsEditMode ? (
                      <label className="manage-drawer-checkbox-label">
                        <input
                          type="checkbox"
                          checked={Boolean(detailsDraft.needsAv)}
                          onChange={(e) => updateDetailsDraft('needsAv', e.target.checked)}
                        />
                        A/V needed
                      </label>
                    ) : (
                      detailsDraft.needsAv ? 'Yes' : 'No'
                    )}
                  </div>
                </div>
                <div className="manage-drawer-table-row">
                  <div className="manage-drawer-table-label">Alcohol</div>
                  <div className="manage-drawer-table-value">
                    {isDetailsEditMode ? (
                      <input
                        type="text"
                        className="manage-drawer-row-input"
                        value={detailsDraft.alcohol}
                        onChange={(e) => updateDetailsDraft('alcohol', e.target.value)}
                        placeholder="Alcohol preference"
                      />
                    ) : (
                      detailsDraft.alcohol || '—'
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {modalTab === TAB_CHANGELOG && (
            <div className="manage-drawer-changelog-list">
              {auditLogLoading ? (
                <p className="manage-drawer-inline-hint">Loading change log…</p>
              ) : null}
              {auditLogError ? (
                <p className="manage-drawer-invoice-error">{auditLogError}</p>
              ) : null}
              {!auditLogLoading && auditLogEntries.length === 0 && !auditLogError ? (
                <p className="manage-drawer-inline-hint">
                  No API changes recorded yet. Updates will appear here with the signed-in email.
                </p>
              ) : null}
              {sortedChangelogEntries.map((entry) => (
                <div key={entry.key} className="manage-drawer-changelog-item">
                  <p className="manage-drawer-changelog-title">{entry.title}</p>
                  <span className="manage-drawer-changelog-time">{entry.detail}</span>
                  {entry.feeLines?.length ? (
                    <ul className="manage-drawer-changelog-fees">
                      {entry.feeLines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
              <div className="manage-drawer-changelog-current-state">
                <p className="manage-drawer-changelog-current-state-label">Current state</p>
                <p className="manage-drawer-changelog-current-state-value">
                  Status: {bookingStatusLabel} · Payment:{' '}
                  {paymentLabels[paymentStr] || paymentLabels[paymentRaw] || 'Pending'}
                </p>
                {currentFeeLines.length ? (
                  <ul className="manage-drawer-changelog-fees">
                    {currentFeeLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
              {recurringInfo.isRecurring ? (
                <div className="manage-drawer-changelog-item">
                  <p className="manage-drawer-changelog-title">Recurring schedule</p>
                  <span className="manage-drawer-changelog-time">
                    {recurringInfo.label || 'Recurring booking'}
                  </span>
                </div>
              ) : null}
            </div>
          )}

          {modalTab === TAB_ADDONS && (
            <div className="manage-drawer-section manage-drawer-section--addons">
              {addonsSaveError ? <p className="manage-drawer-invoice-error">{addonsSaveError}</p> : null}

              {isBookingReadOnly ? (
                sidebarAddonLines.length === 0 ? (
                  <p className="manage-drawer-inline-hint">No add-ons on this booking.</p>
                ) : (
                  <>
                    <p className="manage-drawer-addons-subtotal">
                      Add-ons sub-total: <strong>{formatCurrency(displayAddOnsTotal)}</strong>
                    </p>
                    <div className="manage-drawer-table manage-drawer-table--addons">
                      <div className="manage-drawer-table-header-row">
                        <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--item">Item</div>
                        <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--price">Price / unit</div>
                        <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--qty">Quantity</div>
                        <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--total">Item total</div>
                      </div>
                      {sidebarAddonLines.map((line) => renderStoredAddonReadOnlyRow(line))}
                    </div>
                  </>
                )
              ) : !venueIdForAddons ? (
                <p className="manage-drawer-inline-hint">
                  Add-ons need a venue on this booking — none is linked.
                </p>
              ) : !addonsCatalogReady ? (
                <p className="manage-drawer-inline-hint">Loading venue add-ons…</p>
              ) : venueAddonCatalog.length === 0 && orphanAddonRows.length === 0 ? (
                <p className="manage-drawer-inline-hint">No add-ons configured for this venue.</p>
              ) : (
                <>
                  <p className="manage-drawer-addons-subtotal">
                    Add-ons sub-total: <strong>{formatCurrency(addonsSubtotal)}</strong>
                  </p>
                  <div className="manage-drawer-table manage-drawer-table--addons">
                    <div className="manage-drawer-table-header-row">
                      <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--item">Item</div>
                      <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--price">Price / unit</div>
                      <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--qty">Quantity</div>
                      <div className="manage-drawer-table-header-cell manage-drawer-table-header-cell--total">Item total</div>
                    </div>
                    {activeCatalogAddonRows.map((item) => renderAddonTableRow(item))}
                    {orphanAddonRows.map((row) => renderAddonTableRow(row, { orphan: true }))}
                    {activeCatalogAddonRows.length === 0 && orphanAddonRows.length === 0 && !addonPickerOpen ? (
                      <p className="manage-drawer-addon-empty">No add-ons on this booking yet.</p>
                    ) : null}
                    {renderAddonPickerTile()}
                    {!addonPickerOpen ? (
                      <button
                        type="button"
                        className="manage-drawer-table-add-row"
                        disabled={venueAddonCatalog.length === 0}
                        onClick={openAddonPicker}
                      >
                        <span aria-hidden>+</span>
                        Add another
                      </button>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          )}

          {modalTab === TAB_NOTES && (
            <div className="manage-drawer-section manage-drawer-section--notes">
              <p className="manage-drawer-inline-hint manage-drawer-notes-hint">
                Venue and manual booking notes for this reservation. Customer enquiries and replies appear under{' '}
                <strong>Messages</strong> in the dashboard sidebar.
              </p>
              {notesError ? <p className="manage-drawer-invoice-error">{notesError}</p> : null}
              {!notesOwnerActor ? (
                <p className="manage-drawer-inline-hint">Sign in to view notes.</p>
              ) : (
                <>
                  <div className="manage-drawer-notes-list">
                    {notesLoading ? (
                      <p className="manage-drawer-inline-hint manage-drawer-notes-empty">Loading…</p>
                    ) : bookingNotes.length === 0 ? (
                      <p className="manage-drawer-inline-hint manage-drawer-notes-empty">No notes yet.</p>
                    ) : (
                      bookingNotes.map((note) => {
                        const badge = getBookingNoteBadge(note);
                        const isEditing = editingNoteId === note.id;
                        const editable = !isBookingReadOnly && canEditBookingNote(note);
                        return (
                          <div
                            key={note.id || `${note.created_at}-${note.body?.slice?.(0, 8)}`}
                            className="manage-drawer-note-item"
                          >
                            {isEditing ? (
                              <div className="manage-drawer-note-edit">
                                <textarea
                                  className="manage-drawer-textarea manage-drawer-note-edit-textarea"
                                  value={editingNoteBody}
                                  onChange={(e) => setEditingNoteBody(e.target.value)}
                                  rows={3}
                                  disabled={notesSaving}
                                />
                                <div className="manage-drawer-note-actions">
                                  <button
                                    type="button"
                                    className="manage-drawer-note-action-btn manage-drawer-note-action-btn--secondary"
                                    onClick={handleCancelEditNote}
                                    disabled={notesSaving}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="manage-drawer-note-action-btn manage-drawer-note-action-btn--primary"
                                    onClick={() => handleSaveEditNote(note.id)}
                                    disabled={notesSaving || !editingNoteBody.trim()}
                                  >
                                    {notesSaving ? 'Saving…' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="manage-drawer-note-item-row">
                                <div className="manage-drawer-note-item-text">
                                  <p className="manage-drawer-note-item-body">{note.body}</p>
                                  <span className="manage-drawer-note-item-time">
                                    {formatNotesListTime(note.created_at)}
                                  </span>
                                </div>
                                <div className="manage-drawer-note-item-meta">
                                  <span
                                    className={`manage-drawer-note-badge manage-drawer-note-badge--${badge.variant}`}
                                  >
                                    {badge.label}
                                  </span>
                                  {editable ? (
                                    <button
                                      type="button"
                                      className="manage-drawer-note-edit-btn"
                                      onClick={() => handleStartEditNote(note)}
                                      disabled={notesSaving || Boolean(editingNoteId)}
                                    >
                                      Edit
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  {!isBookingReadOnly ? (
                  <div className="manage-drawer-notes-composer">
                    <label className="manage-drawer-notes-composer-label" htmlFor="manage-drawer-new-note">
                      Add a note
                    </label>
                    <textarea
                      id="manage-drawer-new-note"
                      className="manage-drawer-textarea"
                      placeholder="Internal note for this booking…"
                      value={newNoteBody}
                      onChange={(e) => setNewNoteBody(e.target.value)}
                      rows={3}
                      disabled={notesSaving}
                    />
                    <button
                      type="button"
                      className="manage-drawer-save-addons-btn"
                      onClick={handleAddNote}
                      disabled={notesSaving || !newNoteBody.trim()}
                    >
                      {notesSaving ? 'Saving…' : 'Add note'}
                    </button>
                  </div>
                  ) : null}
                </>
              )}
            </div>
          )}
        </div>

            {modalTab === TAB_BOOKING && !isBookingReadOnly && !isPaymentEditMode && (hasBookingDetailsDraftChanges || isDetailsEditMode) ? (
              <div className="manage-drawer-tab-footer">
                {hasBookingDetailsDraftChanges ? (
                  <p className="manage-drawer-inline-hint">
                    You have unsaved booking changes.
                  </p>
                ) : null}
                {isDetailsEditMode ? (
                  <div className="manage-drawer-table-actions">
                    <button
                      type="button"
                      className="manage-drawer-table-action-btn manage-drawer-table-action-btn--cancel"
                      onClick={handleCancelDetailsEdit}
                      disabled={isConfirming || addonsSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="manage-drawer-table-action-btn manage-drawer-table-action-btn--save"
                      onClick={handleSaveDetails}
                      disabled={!hasBookingDetailsDraftChanges || isConfirming || addonsSaving}
                    >
                      Save
                    </button>
                  </div>
                ) : hasBookingDetailsDraftChanges ? (
                  <div className="manage-drawer-table-actions">
                    <button
                      type="button"
                      className="manage-drawer-table-action-btn manage-drawer-table-action-btn--cancel"
                      onClick={() => {
                        applyBookingToDraft(booking);
                        setDetailsSaveError('');
                      }}
                      disabled={isConfirming || addonsSaving}
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      className="manage-drawer-table-action-btn manage-drawer-table-action-btn--save"
                      onClick={handleSaveDetails}
                      disabled={isConfirming || addonsSaving}
                    >
                      Save changes
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {modalTab === TAB_ADDONS && !isBookingReadOnly && !isPaymentEditMode && hasAddOnsDraftChanges ? (
              <div className="manage-drawer-tab-footer">
                <p className="manage-drawer-inline-hint">
                  You have unsaved add-on changes.
                </p>
                <div className="manage-drawer-table-actions">
                  <button
                    type="button"
                    className="manage-drawer-table-action-btn manage-drawer-table-action-btn--cancel"
                    onClick={() => {
                      applyBookingToDraft(booking);
                      setAddonsSaveError('');
                      setDetailsSaveError('');
                    }}
                    disabled={addonsSaving}
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    className="manage-drawer-table-action-btn manage-drawer-table-action-btn--save"
                    onClick={handleSaveAddons}
                    disabled={addonsSaving}
                  >
                    {addonsSaving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            ) : null}

            {showEnquiryFooter && !isBookingReadOnly && !isPaymentEditMode && hasLeftPanelDraftChanges && modalTab !== TAB_BOOKING && modalTab !== TAB_ADDONS && !isDetailsEditMode ? (
              <div className="manage-drawer-tab-footer">
                <p className="manage-drawer-inline-hint">
                  You have unsaved booking changes. Save before accepting, or they will be saved
                  automatically when you accept.
                </p>
                <div className="manage-drawer-table-actions">
                  <button
                    type="button"
                    className="manage-drawer-table-action-btn manage-drawer-table-action-btn--cancel"
                    onClick={() => {
                      applyBookingToDraft(booking);
                      setDetailsSaveError('');
                      setDiscountError('');
                    }}
                    disabled={isConfirming || addonsSaving}
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    className="manage-drawer-table-action-btn manage-drawer-table-action-btn--save"
                    onClick={handleSaveDetails}
                    disabled={isConfirming || addonsSaving}
                  >
                    {isConfirming ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            ) : null}

            {showEnquiryFooter ? (
              <div className="manage-drawer-enquiry-footer">
                {canMessageOrganizer ? (
                  <button
                    type="button"
                    className="manage-drawer-message-btn"
                    disabled={isConfirming || addonsSaving || openingMessageThread}
                    onClick={handleStartOrganizerMessage}
                  >
                    {openingMessageThread ? 'Opening…' : 'Message organizer'}
                  </button>
                ) : (
                  <p className="manage-drawer-enquiry-label">Respond to this enquiry</p>
                )}
                <div className="manage-drawer-enquiry-actions">
                  <button
                    type="button"
                    className="manage-drawer-enquiry-btn manage-drawer-enquiry-btn--hold"
                    disabled={isConfirming || addonsSaving}
                    onClick={() => handleEnquiryAction('on_hold')}
                  >
                    Put on hold
                  </button>
                  <button
                    type="button"
                    className="manage-drawer-enquiry-btn manage-drawer-enquiry-btn--reject"
                    disabled={isConfirming || addonsSaving}
                    onClick={() => handleEnquiryAction('canceled_by_owner')}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="manage-drawer-enquiry-btn manage-drawer-enquiry-btn--accept"
                    disabled={isConfirming || addonsSaving}
                    onClick={() => handleEnquiryAction('confirmed')}
                  >
                    {isConfirming ? 'Saving…' : 'Accept'}
                  </button>
                </div>
              </div>
            ) : canCancelBooking || canMessageOrganizer ? (
              <div className="manage-drawer-cancel-footer">
                {canMessageOrganizer ? (
                  <button
                    type="button"
                    className="manage-drawer-message-btn"
                    disabled={isConfirming || addonsSaving || openingMessageThread}
                    onClick={handleStartOrganizerMessage}
                  >
                    {openingMessageThread ? 'Opening…' : 'Message organizer'}
                  </button>
                ) : null}
                {canCancelBooking ? (
                  <button
                    type="button"
                    className="manage-drawer-cancel-btn"
                    disabled={isConfirming || addonsSaving || openingMessageThread}
                    onClick={() => setCancelConfirmOpen(true)}
                  >
                    Cancel booking
                  </button>
                ) : null}
              </div>
            ) : null}
            {showEnquiryFooter ? (
              <div className="manage-drawer-message-field" style={{ padding: '0 24px 16px' }}>
                <div className="manage-drawer-message-label-row">
                  <span className="manage-drawer-field-label">Message to customer (optional)</span>
                  <span className="manage-drawer-char-count">
                    {customerMessage.length}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
                <textarea
                  className="manage-drawer-textarea"
                  placeholder="Enter message"
                  value={customerMessage}
                  onChange={(e) => {
                    if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                      setCustomerMessage(e.target.value);
                    }
                  }}
                  maxLength={MAX_MESSAGE_LENGTH}
                  rows={2}
                />
              </div>
            ) : null}
            {detailsSaveError ? (
              <p className="manage-drawer-footer-error" style={{ padding: '0 24px 12px' }}>{detailsSaveError}</p>
            ) : null}
            {approvalFeedback ? (
              <p className={`manage-drawer-footer-error${approvalFeedback.includes('sent to the customer') ? ' manage-drawer-footer-success' : ''}`} style={{ padding: '0 24px 12px' }}>
                {approvalFeedback}
              </p>
            ) : null}
          </div>
          ) : null}

          {(!isMobileSection || showMobilePricePanel) ? (
          <aside
            className={`manage-drawer-sidebar${
              showMobilePricePanel ? ' manage-drawer-sidebar--mobile-primary' : ''
            }`}
          >
            <div className="manage-drawer-sidebar-section manage-drawer-sidebar-section--price-details">
              {!showMobilePricePanel ? (
              <p className="manage-drawer-sidebar-header-title manage-drawer-sidebar-header-title--static">
                Price details
              </p>
              ) : null}

              {renderPaymentReminderSection()}
              {renderFeedbackSection()}

              {!showMobilePricePanel ? (
              <div className="manage-drawer-price-tabs" role="tablist" aria-label="Price details">
                <button
                  type="button"
                  role="tab"
                  aria-selected={priceDetailsTab === PRICE_TAB_BREAKDOWN}
                  className={`manage-drawer-price-tab${priceDetailsTab === PRICE_TAB_BREAKDOWN ? ' manage-drawer-price-tab--active' : ''}`}
                  onClick={() => setPriceDetailsTab(PRICE_TAB_BREAKDOWN)}
                >
                  <PriceBreakdownTabIcon />
                  Price breakdown
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={priceDetailsTab === PRICE_TAB_PAYMENT}
                  className={`manage-drawer-price-tab${priceDetailsTab === PRICE_TAB_PAYMENT ? ' manage-drawer-price-tab--active' : ''}`}
                  onClick={() => setPriceDetailsTab(PRICE_TAB_PAYMENT)}
                >
                  <PaymentCardIcon />
                  Payment details
                </button>
              </div>
              ) : null}

              <div className="manage-drawer-price-tab-panel">
                {priceDetailsTab === PRICE_TAB_BREAKDOWN ? (
                  <>
                    {renderFreeSpaceWaiver()}
                    <div className="manage-drawer-price-rows">{renderPriceBreakdownContent()}</div>
                    {canOpenPriceEdit ? (
                      <div className="manage-drawer-price-footer">
                        <button
                          type="button"
                          className="manage-drawer-price-outline-btn"
                          onClick={() => setIsPriceEditMode(true)}
                        >
                          Edit price breakdown
                        </button>
                      </div>
                    ) : null}
                    {(isPriceEditMode || hasRightPanelPriceDraftChanges) && !isBookingReadOnly && !isPastBooking ? (
                      <div className="manage-drawer-price-footer manage-drawer-price-footer--actions">
                        <button
                          type="button"
                          className="manage-drawer-table-action-btn manage-drawer-table-action-btn--cancel"
                          onClick={handleCancelPriceEdit}
                          disabled={isConfirming || addonsSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="manage-drawer-table-action-btn manage-drawer-table-action-btn--save"
                          onClick={handleSavePriceChanges}
                          disabled={isConfirming || addonsSaving}
                        >
                          {isConfirming ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : !paymentDetailsAvailable ? (
                  <div className="manage-drawer-payment-empty">
                    <PaymentEmptyIcon />
                    <p className="manage-drawer-payment-empty-text">
                      Payment details become available once this booking is accepted.
                    </p>
                  </div>
                ) : showPaymentCollectionPanel && !isPaymentEditMode ? (
                  <div className="manage-drawer-section manage-drawer-section--payment-collect">
                    <div className="manage-drawer-payment-collect">
                      <div className="manage-drawer-payment-collect-banner">
                        Choose how you wish to collect payment
                      </div>
                      <div
                        className={`manage-drawer-payment-collect-option${
                          paymentCollectionMethod === 'online'
                            ? ' manage-drawer-payment-collect-option--selected'
                            : ''
                        }`}
                      >
                        <label className="manage-drawer-payment-collect-option-label">
                          <input
                            type="radio"
                            name="paymentCollectionMethod"
                            value="online"
                            checked={paymentCollectionMethod === 'online'}
                            onChange={() => setPaymentCollectionMethod('online')}
                            className="manage-drawer-radio-input"
                          />
                          <span className="manage-drawer-radio-custom" />
                          <div className="manage-drawer-payment-collect-option-body">
                            <div className="manage-drawer-payment-collect-option-head">
                              <div className="manage-drawer-payment-collect-option-title-row">
                                <span className="manage-drawer-payment-collect-option-title">
                                  {onlinePaymentCopy.title}
                                </span>
                                {onlinePaymentCopy.showStripeLogo ? (
                                  <img
                                    src="/manual-booking/icons/stripe-logo.png"
                                    alt="Stripe"
                                    className="manage-drawer-payment-collect-stripe-logo"
                                  />
                                ) : null}
                              </div>
                              {onlinePaymentCopy.recommended ? (
                                <span className="manage-drawer-payment-collect-badge">Recommended</span>
                              ) : null}
                            </div>
                            <p className="manage-drawer-payment-collect-description">
                              {onlinePaymentCopy.description}
                            </p>
                            {paymentCollectionMethod === 'online' ? (
                              <div className="manage-drawer-payment-collect-send-row">
                                <button
                                  type="button"
                                  className="manage-drawer-payment-collect-send-btn"
                                  disabled={sendingReminder || paymentContextLoading}
                                  onClick={handleSendPaymentReminder}
                                >
                                  {sendingReminder
                                    ? 'Sending…'
                                    : paymentContextLoading
                                      ? 'Loading…'
                                      : 'Send payment link'}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </label>
                      </div>
                      <div
                        className={`manage-drawer-payment-collect-option${
                          paymentCollectionMethod === 'outside'
                            ? ' manage-drawer-payment-collect-option--selected'
                            : ''
                        }`}
                      >
                        <label className="manage-drawer-payment-collect-option-label">
                          <input
                            type="radio"
                            name="paymentCollectionMethod"
                            value="outside"
                            checked={paymentCollectionMethod === 'outside'}
                            onChange={() => setPaymentCollectionMethod('outside')}
                            className="manage-drawer-radio-input"
                          />
                          <span className="manage-drawer-radio-custom" />
                          <div className="manage-drawer-payment-collect-option-body">
                            <span className="manage-drawer-payment-collect-option-title">
                              Paid outside the platform
                            </span>
                            <p className="manage-drawer-payment-collect-description">
                              Cash, bank transfer, or another method received directly.
                            </p>
                            {paymentCollectionMethod === 'outside' ? (
                              <button
                                type="button"
                                className="manage-drawer-payment-collect-send-btn"
                                disabled={isConfirming}
                                onClick={handleMarkPaidOutside}
                              >
                                Mark as paid
                              </button>
                            ) : null}
                          </div>
                        </label>
                      </div>
                    </div>
                    {paymentContext?.mode === 'invoice' ? (
                      <div className="manage-drawer-invoice manage-drawer-invoice--compact">
                        {renderPaymentInvoiceField()}
                      </div>
                    ) : null}
                    {canOpenPaymentEdit ? (
                      <div className="manage-drawer-price-footer">
                        <button
                          type="button"
                          className="manage-drawer-price-outline-btn"
                          onClick={() => setIsPaymentEditMode(true)}
                        >
                          Edit payment details
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : isPaymentEditMode ? (
                  <>
                    {renderPaymentDetailsEditForm()}
                    <div className="manage-drawer-price-footer manage-drawer-price-footer--actions">
                      <button
                        type="button"
                        className="manage-drawer-table-action-btn manage-drawer-table-action-btn--cancel"
                        onClick={handleCancelPaymentEdit}
                        disabled={isConfirming || addonsSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="manage-drawer-table-action-btn manage-drawer-table-action-btn--save"
                        onClick={handleSavePaymentChanges}
                        disabled={isConfirming || addonsSaving}
                      >
                        {isConfirming ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : bookingIsFree && bookingIsPaid ? (
                  <p className="manage-drawer-inline-hint">No payment required for this booking.</p>
                ) : (
                  <>
                    {renderPaymentDetailsViewRows()}
                    {canOpenPaymentEdit ? (
                      <div className="manage-drawer-price-footer">
                        <button
                          type="button"
                          className="manage-drawer-price-outline-btn"
                          onClick={() => setIsPaymentEditMode(true)}
                        >
                          Edit payment details
                        </button>
                      </div>
                    ) : null}
                    {renderPaymentStatusUpdateSection()}
                  </>
                )}
                {detailsSaveError && priceDetailsTab === PRICE_TAB_PAYMENT ? (
                  <p className="manage-drawer-invoice-error">{detailsSaveError}</p>
                ) : null}
              </div>
            </div>
          </aside>
          ) : null}
        </div>

        {cancelConfirmOpen ? (
          <div
            className="manage-drawer-unsaved-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-drawer-cancel-title"
            onClick={(e) => {
              if (e.target === e.currentTarget && !isConfirming) setCancelConfirmOpen(false);
            }}
          >
            <div className="manage-drawer-unsaved-dialog">
              <h3 id="manage-drawer-cancel-title" className="manage-drawer-unsaved-title">
                {showEnquiryFooter ? 'Reject this enquiry?' : 'Cancel this booking?'}
              </h3>
              <p className="manage-drawer-unsaved-body">
                {showEnquiryFooter
                  ? 'The customer will be notified by email that their enquiry was declined.'
                  : 'The customer will be notified by email that this booking has been cancelled.'}{' '}
                This action cannot be undone.
              </p>
              <div className="manage-drawer-cancel-message-field">
                <div className="manage-drawer-message-label-row">
                  <span className="manage-drawer-field-label">Message to customer (optional)</span>
                  <span className="manage-drawer-char-count">
                    {customerMessage.length}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
                <textarea
                  className="manage-drawer-textarea"
                  placeholder="Enter message"
                  value={customerMessage}
                  onChange={(e) => {
                    if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                      setCustomerMessage(e.target.value);
                    }
                  }}
                  maxLength={MAX_MESSAGE_LENGTH}
                  rows={3}
                />
              </div>
              <div className="manage-drawer-unsaved-actions">
                <button
                  type="button"
                  className="manage-drawer-unsaved-btn"
                  onClick={() => setCancelConfirmOpen(false)}
                  disabled={isConfirming}
                >
                  Keep booking
                </button>
                <button
                  type="button"
                  className="manage-drawer-unsaved-btn manage-drawer-unsaved-btn--danger"
                  onClick={handleConfirmCancelBooking}
                  disabled={isConfirming}
                >
                  {isConfirming ? 'Cancelling…' : showEnquiryFooter ? 'Reject enquiry' : 'Cancel booking'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {closePromptOpen ? (
          <div className="manage-drawer-unsaved-overlay" role="dialog" aria-modal="true" aria-labelledby="manage-drawer-unsaved-title">
            <div className="manage-drawer-unsaved-dialog">
              <h3 id="manage-drawer-unsaved-title" className="manage-drawer-unsaved-title">
                Unsaved changes
              </h3>
              <p className="manage-drawer-unsaved-body">
                Save all pending changes before closing this booking?
              </p>
              {pendingDraftChanges.length > 0 ? (
                <div className="manage-drawer-unsaved-changes">
                  <p className="manage-drawer-unsaved-changes-heading">What will change</p>
                  <ul className="manage-drawer-unsaved-changes-list">
                    {pendingDraftChanges.map((change) => (
                      <li key={change.label} className="manage-drawer-unsaved-change-item">
                        <span className="manage-drawer-unsaved-change-label">{change.label}</span>
                        <span className="manage-drawer-unsaved-change-values">
                          {change.before} → {change.after}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="manage-drawer-unsaved-actions">
                <button
                  type="button"
                  className="manage-drawer-unsaved-btn"
                  onClick={handleClosePromptCancel}
                  disabled={closePromptBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="manage-drawer-unsaved-btn manage-drawer-unsaved-btn--discard"
                  onClick={handleClosePromptDiscard}
                  disabled={closePromptBusy}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="manage-drawer-unsaved-btn manage-drawer-unsaved-btn--primary"
                  onClick={handleClosePromptSave}
                  disabled={closePromptBusy}
                >
                  {closePromptBusy ? 'Saving…' : 'Save all'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ManageBookingDrawer;
